import { useState, useMemo, useRef, useEffect, createContext, useContext } from "react";
import {
  Search, ArrowLeft, Phone, Mail, FileText, Wrench, CheckCircle2,
  Building2, MapPin, Calendar, Plus, RefreshCw, LayoutDashboard, Users,
  Wallet, ListChecks, BarChart3, Settings, Smartphone, Bell, Download,
  Landmark, ClipboardCheck, AlertTriangle, Receipt, UserPlus, Save, Trash2, LogOut,
  Sparkles, Send, Upload, ChevronDown, ChevronRight, MessageCircle,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, Tooltip,
  PieChart, Pie, AreaChart, Area,
} from "recharts";
import Tesseract from "tesseract.js";
import html2pdf from "html2pdf.js";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { dbEnabled, loadCustomers, saveCustomer, deleteCustomer, seedCustomers, loadPlans, savePlan, deletePlan, seedPlans, loadTasks, saveTask, deleteTask, seedTasks, subscribe } from "./db";
import { MILESTONE_SEED } from "./milestoneSeed";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Render the first page of a PDF to a canvas so OCR can read it.
async function pdfFirstPageToCanvas(file) {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 2 }); // upscale for sharper OCR
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  return canvas;
}

const LOGO = (process.env.PUBLIC_URL || "") + "/rohan-logo.jpeg";
// Locked letterhead logo (do not change) — used by the demand letter header.
const LETTERHEAD_LOGO = LOGO;
const _OLD_LOGO = "data:image/png;base64,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";

/* ---------------- theme ---------------- */
const C = {
  paper: "#F7F5F1", card: "#F4F1EA", white: "#FFFFFF", ink: "#4A3520",
  muted: "#7C6F5E", faint: "#A99E8C", line: "#EAE5DB",
  accent: "#6E4B1E", accentSoft: "#F0E8DA", lime: "#C2A26A", limeSoft: "#F3ECDD",
  amber: "#B0780F", amberSoft: "#F6ECD6", red: "#C0392B", redSoft: "#F4E2DC", green: "#3B7A4E", greenSoft: "#E6F0E6",
  sidebar: "#FFFFFF",
};
const PIE = ["#4A3214", "#6E4B1E", "#8A6326", "#A98445", "#C2A26A", "#E0CFA8"];
const display = "'Fraunces', Georgia, serif";
const sans = "'Archivo', ui-sans-serif, system-ui, sans-serif";
const STAGES = ["Booked", "Agreement", "Registration", "Construction", "Possession", "Handover"];
const inr = (n) => "₹" + new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

/* ---------------- Ask AI (preview) ---------------- */
const AI_SUGGESTIONS = [
  "Draft a payment reminder for an overdue installment",
  "Write a possession handover message to a customer",
  "Summarise a customer call note into action items",
  "Explain stamp duty and registration charges simply",
  "Draft a WhatsApp message for a milestone demand",
  "Write a welcome message for a new booking",
  "List documents needed for plot registration",
  "Suggest a follow-up plan for a 60+ days overdue customer",
];
// Canned sample replies so the preview feels alive (no live model yet).
const AI_REPLIES = {
  "Draft a payment reminder for an overdue installment":
    "Subject: Gentle reminder — pending installment\n\nDear {name},\n\nThis is a friendly reminder that your installment of ₹{amount} for {project} ({unit}) was due on {dueDate} and is currently overdue by {days} days. A penalty of 1.5% per month applies on overdue dues.\n\nPlease arrange payment at the earliest. Reply here if you'd like the payment link or have any questions.\n\nWarm regards,\nRohan Corporation — Collections",
  "Write a possession handover message to a customer":
    "Dear {name},\n\nCongratulations! We're delighted to inform you that your {unit} at {project} is ready for possession. 🎉\n\nPlease schedule your handover visit at your convenience. Kindly carry a valid ID and the final payment receipt. Our team will walk you through the unit, hand over the keys, and complete the snag check.\n\nWelcome home!\nRohan Corporation",
  "Summarise a customer call note into action items":
    "Paste the call note and I'll convert it into action items. Example output:\n\n• Follow up on overdue foundation demand — Owner: Accounts — Due: this week\n• Share HDFC disbursement checklist — Owner: CRM\n• Schedule pre-handover inspection — Owner: Site\n• Send registration slot options — Owner: Legal",
  "Explain stamp duty and registration charges simply":
    "Stamp duty is a state tax on a property transaction — proof that the sale is legally recorded. In Karnataka it's typically ~5% of the property value (plus ~0.5% cess/surcharge). Registration charge is a separate ~1% fee to enter the deed in government records.\n\nExample on ₹50,00,000: stamp duty ≈ ₹2,75,000 + registration ≈ ₹50,000. Both are paid by the buyer at registration.",
  "Draft a WhatsApp message for a milestone demand":
    "Hi {name} 👋\n\nUpdate on {project}: the *{milestone}* stage is complete, so the linked installment of ₹{amount} is now due ({dueDate}).\n\nYou can pay via the link we'll share, or reply here and we'll assist. Thank you!\n— Rohan Corporation",
  "Write a welcome message for a new booking":
    "Dear {name},\n\nThank you for booking {unit} at {project} with Rohan Corporation! 🏡\n\nYour booking number is {bookingNo}. Your dedicated relationship manager will reach out shortly with the agreement, payment schedule, and next steps. We're thrilled to have you with us.\n\nWarm regards,\nRohan Corporation",
  "List documents needed for plot registration":
    "Documents typically needed for plot registration:\n\n• Sale agreement (signed)\n• PAN + Aadhaar of buyer(s)\n• Recent passport-size photographs\n• Encumbrance Certificate (EC)\n• Khata certificate & extract\n• Latest property tax paid receipt\n• Approved layout / release plan\n• Bank loan sanction letter (if applicable)\n• Stamp duty & registration fee payment proof",
  "Suggest a follow-up plan for a 60+ days overdue customer":
    "Suggested escalation plan for 60+ days overdue:\n\n1. Day 0 — Polite WhatsApp + SMS reminder with payment link.\n2. Day 2 — Call from relationship manager; understand the delay.\n3. Day 5 — Formal demand letter (email + courier) noting penalty accrual.\n4. Day 10 — Manager call; offer a short, documented payment plan if genuine hardship.\n5. Day 15 — Final notice referencing agreement clauses before further action.\n\nLog every contact on the customer timeline.",
};
function replyFor(text) {
  if (AI_REPLIES[text]) return AI_REPLIES[text];
  const t = text.toLowerCase();
  if (/remind|overdue|payment due/.test(t)) return AI_REPLIES["Draft a payment reminder for an overdue installment"];
  if (/handover|possession/.test(t)) return AI_REPLIES["Write a possession handover message to a customer"];
  if (/summar|action item|call note/.test(t)) return AI_REPLIES["Summarise a customer call note into action items"];
  if (/stamp duty|registration charge/.test(t)) return AI_REPLIES["Explain stamp duty and registration charges simply"];
  if (/whatsapp|milestone|demand/.test(t)) return AI_REPLIES["Draft a WhatsApp message for a milestone demand"];
  if (/welcome|new booking/.test(t)) return AI_REPLIES["Write a welcome message for a new booking"];
  if (/document|registration/.test(t)) return AI_REPLIES["List documents needed for plot registration"];
  if (/follow.?up|escalat/.test(t)) return AI_REPLIES["Suggest a follow-up plan for a 60+ days overdue customer"];
  return "This is a preview of the Ask AI assistant. Here's a sample response — connect a live model to get tailored answers grounded in your customer data.";
}
const penaltyOf = (s) => (s.status === "overdue" && s.daysLate ? Math.round(s.amt * 0.00045 * s.daysLate) : 0);

/* ---------------- seed data ---------------- */
const SEED_BASE = [
  {
    id: 1, bookingNo: "BK-2025-0011", name: "Ananya Rao", phone: "98450 11234", email: "ananya.rao@email.com",
    project: "Brigade Eldorado", type: "Flat", unit: "A-1204", area: "1,340 sqft", facing: "East",
    salePrice: 11800000, salesperson: "Deepak K", saleDate: "12 Aug 2025", stageIdx: 3,
    loan: { bank: "SBI", sanctioned: 9000000, disbursed: 3540000, status: "partial" },
    registration: null,
    schedule: [
      { label: "Booking amount", due: "12 Aug 2025", amt: 1180000, status: "paid", on: "12 Aug 2025", receipt: "RCPT-1011" },
      { label: "On agreement", due: "05 Sep 2025", amt: 2360000, status: "paid", on: "04 Sep 2025", receipt: "RCPT-1042" },
      { label: "On foundation", due: "20 Apr 2026", amt: 3540000, status: "overdue", daysLate: 42 },
      { label: "On 5th slab", due: "30 Jul 2026", amt: 2950000, status: "due" },
      { label: "On possession", due: "15 Dec 2026", amt: 1770000, status: "due" },
    ],
    docs: [{ name: "Sale agreement", status: "signed" }, { name: "PAN + Aadhaar", status: "received" }, { name: "Allotment letter", status: "pending" }],
    futureReqs: [{ item: "Clear overdue foundation demand", when: "Due now" }, { item: "SBI loan next disbursement", when: "On 5th slab" }, { item: "Possession & handover planning", when: "Dec 2026" }],
    snags: [], timeline: [{ date: "21 Apr 2026", text: "Foundation demand note sent; overdue", by: "Accounts" }], service: [],
  },
  {
    id: 2, bookingNo: "BK-2026-0007", name: "Vikram Shetty", phone: "99860 44521", email: "vikram.s@email.com",
    project: "Prestige Glenwood", type: "Villa", unit: "V-07", area: "2,950 sqft", facing: "North",
    salePrice: 32500000, salesperson: "Reena M", saleDate: "02 Feb 2026", stageIdx: 2,
    loan: { bank: "HDFC", sanctioned: 22000000, disbursed: 9750000, status: "partial" },
    registration: { status: "scheduled", date: "10 Jun 2026", stampDuty: 1950000 },
    schedule: [
      { label: "Booking amount", due: "02 Feb 2026", amt: 3250000, status: "paid", on: "02 Feb 2026", receipt: "RCPT-1188" },
      { label: "On agreement", due: "28 Feb 2026", amt: 9750000, status: "paid", on: "26 Feb 2026", receipt: "RCPT-1203" },
      { label: "On registration", due: "10 Jun 2026", amt: 13000000, status: "due" },
      { label: "On handover", due: "20 Nov 2026", amt: 6500000, status: "due" },
    ],
    docs: [{ name: "Sale agreement", status: "signed" }, { name: "PAN + Aadhaar", status: "received" }, { name: "Loan sanction (HDFC)", status: "received" }],
    futureReqs: [{ item: "Complete registration formalities", when: "10 Jun 2026" }, { item: "HDFC disbursement for registration", when: "Jun 2026" }, { item: "Pre-handover inspection", when: "Nov 2026" }],
    snags: [], timeline: [{ date: "15 May 2026", text: "Registration slot proposed: 10 Jun", by: "CRM" }], service: [],
  },
  {
    id: 3, bookingNo: "BK-2025-0003", name: "Fatima Khan", phone: "98801 77390", email: "fatima.k@email.com",
    project: "Sunrise Layout", type: "Plot", unit: "P-142", area: "30x40 (1,200 sqft)", facing: "West",
    salePrice: 6500000, salesperson: "Deepak K", saleDate: "18 Mar 2025", stageIdx: 4,
    loan: null, registration: { status: "registered", date: "22 Jul 2025", stampDuty: 390000 },
    schedule: [
      { label: "Booking amount", due: "18 Mar 2025", amt: 650000, status: "paid", on: "18 Mar 2025", receipt: "RCPT-0912" },
      { label: "On agreement", due: "10 Apr 2025", amt: 2925000, status: "paid", on: "09 Apr 2025", receipt: "RCPT-0934" },
      { label: "On registration", due: "22 Jul 2025", amt: 2925000, status: "paid", on: "22 Jul 2025", receipt: "RCPT-0998" },
    ],
    docs: [{ name: "Sale deed (registered)", status: "signed" }, { name: "PAN + Aadhaar", status: "received" }, { name: "Khata transfer", status: "pending" }],
    futureReqs: [{ item: "Complete Khata transfer", when: "In progress" }, { item: "Boundary marking & fencing", when: "Pending" }, { item: "Site possession handover", when: "Jul 2026" }],
    snags: [{ item: "Boundary marking pending", status: "open" }],
    timeline: [{ date: "02 May 2026", text: "Possession offered; awaiting Khata transfer", by: "CRM" }], service: [],
  },
  {
    id: 4, bookingNo: "BK-2026-0015", name: "Rohan Mehta", phone: "97411 20985", email: "rohan.m@email.com",
    project: "Brigade Eldorado", type: "Flat", unit: "B-0905", area: "1,180 sqft", facing: "South",
    salePrice: 9900000, salesperson: "Reena M", saleDate: "08 May 2026", stageIdx: 1,
    loan: { bank: "ICICI", sanctioned: 7500000, disbursed: 0, status: "sanctioned" }, registration: null,
    schedule: [
      { label: "Booking amount", due: "08 May 2026", amt: 990000, status: "paid", on: "08 May 2026", receipt: "RCPT-1240" },
      { label: "On agreement", due: "05 Jun 2026", amt: 1980000, status: "due" },
      { label: "On foundation", due: "30 Sep 2026", amt: 3960000, status: "due" },
      { label: "On possession", due: "20 Mar 2027", amt: 2970000, status: "due" },
    ],
    docs: [{ name: "Sale agreement", status: "pending" }, { name: "PAN + Aadhaar", status: "received" }],
    futureReqs: [{ item: "Sign sale agreement", when: "Due now" }, { item: "ICICI disbursement schedule", when: "Sep 2026" }, { item: "Interior customization preferences", when: "Before fit-out" }],
    snags: [], timeline: [{ date: "08 May 2026", text: "Booking confirmed, welcome call done", by: "CRM" }], service: [],
  },
  {
    id: 5, bookingNo: "BK-2024-0042", name: "Lakshmi Nair", phone: "99000 56712", email: "lakshmi.n@email.com",
    project: "Prestige Glenwood", type: "Villa", unit: "V-12", area: "3,100 sqft", facing: "East",
    salePrice: 34000000, salesperson: "Deepak K", saleDate: "10 Jan 2024", stageIdx: 5,
    loan: { bank: "Axis", sanctioned: 24000000, disbursed: 24000000, status: "complete" },
    registration: { status: "registered", date: "15 May 2024", stampDuty: 2040000 },
    schedule: [
      { label: "Booking + agreement", due: "10 Jan 2024", amt: 10200000, status: "paid", on: "10 Jan 2024", receipt: "RCPT-0610" },
      { label: "On registration", due: "15 May 2024", amt: 13600000, status: "paid", on: "15 May 2024", receipt: "RCPT-0701" },
      { label: "On handover", due: "20 Dec 2025", amt: 10200000, status: "paid", on: "18 Dec 2025", receipt: "RCPT-0989" },
    ],
    docs: [{ name: "Sale deed (registered)", status: "signed" }, { name: "Handover checklist", status: "signed" }, { name: "Occupancy certificate", status: "received" }],
    futureReqs: [{ item: "Resolve balcony seepage snag", when: "Open" }, { item: "Defect-liability follow-ups", when: "Ongoing" }, { item: "Khata & property-tax setup", when: "Pending" }],
    snags: [{ item: "Balcony seepage", status: "open" }, { item: "Kitchen hinge", status: "resolved" }],
    timeline: [{ date: "18 Dec 2025", text: "Possession handed over, keys issued", by: "CRM" }],
    service: [{ title: "Seepage in master balcony", status: "in-progress", date: "14 Apr 2026" }, { title: "Modular kitchen hinge fix", status: "resolved", date: "02 Feb 2026" }],
  },
  {
    id: 6, bookingNo: "BK-2025-0028", name: "Arjun Pillai", phone: "98447 33108", email: "arjun.p@email.com",
    project: "Sunrise Layout", type: "Plot", unit: "P-118", area: "40x60 (2,400 sqft)", facing: "North",
    salePrice: 13000000, salesperson: "Reena M", saleDate: "25 Nov 2025", stageIdx: 3,
    loan: null, registration: null,
    schedule: [
      { label: "Booking amount", due: "25 Nov 2025", amt: 1300000, status: "paid", on: "25 Nov 2025", receipt: "RCPT-1099" },
      { label: "On agreement", due: "20 Dec 2025", amt: 3900000, status: "paid", on: "19 Dec 2025", receipt: "RCPT-1120" },
      { label: "On development", due: "10 May 2026", amt: 5200000, status: "overdue", daysLate: 22 },
      { label: "On registration", due: "30 Aug 2026", amt: 2600000, status: "due" },
    ],
    docs: [{ name: "Sale agreement", status: "signed" }, { name: "PAN + Aadhaar", status: "received" }],
    futureReqs: [{ item: "Clear overdue development payment", when: "Due now" }, { item: "Plot registration", when: "30 Aug 2026" }, { item: "Khata transfer post-registration", when: "After Aug 2026" }],
    snags: [], timeline: [{ date: "12 May 2026", text: "Development payment overdue, reminder sent", by: "Accounts" }], service: [],
  },
  {
    id: 7, bookingNo: "BK-2026-0021", name: "Sneha Iyer", phone: "98453 88210", email: "sneha.iyer@email.com",
    project: "Brigade Eldorado", type: "Flat", unit: "C-1502", area: "1,520 sqft", facing: "East",
    salePrice: 14500000, salesperson: "Deepak K", saleDate: "14 Mar 2026", stageIdx: 2,
    loan: { bank: "Kotak", sanctioned: 10000000, disbursed: 4350000, status: "partial" },
    registration: { status: "scheduled", date: "25 Jul 2026", stampDuty: 870000 },
    schedule: [
      { label: "Booking amount", due: "14 Mar 2026", amt: 1450000, status: "paid", on: "14 Mar 2026", receipt: "RCPT-1260" },
      { label: "On agreement", due: "08 Apr 2026", amt: 2900000, status: "paid", on: "07 Apr 2026", receipt: "RCPT-1281" },
      { label: "On registration", due: "25 Jul 2026", amt: 5800000, status: "due" },
      { label: "On 8th slab", due: "15 Oct 2026", amt: 2900000, status: "due" },
      { label: "On possession", due: "30 Apr 2027", amt: 1450000, status: "due" },
    ],
    docs: [{ name: "Sale agreement", status: "signed" }, { name: "PAN + Aadhaar", status: "received" }, { name: "Loan sanction (Kotak)", status: "received" }],
    futureReqs: [{ item: "Complete registration", when: "25 Jul 2026" }, { item: "Kotak next disbursement", when: "On 8th slab" }, { item: "Interior fit-out planning", when: "2027" }],
    snags: [], timeline: [{ date: "10 Apr 2026", text: "Agreement signed; registration slot to be booked", by: "CRM" }], service: [],
  },
  {
    id: 8, bookingNo: "BK-2025-0019", name: "Imran Sheikh", phone: "99021 47765", email: "imran.s@email.com",
    project: "Sunrise Layout", type: "Plot", unit: "P-097", area: "30x50 (1,500 sqft)", facing: "South",
    salePrice: 8200000, salesperson: "Reena M", saleDate: "06 Sep 2025", stageIdx: 3,
    loan: null, registration: null,
    schedule: [
      { label: "Booking amount", due: "06 Sep 2025", amt: 820000, status: "paid", on: "06 Sep 2025", receipt: "RCPT-1055" },
      { label: "On agreement", due: "01 Oct 2025", amt: 2460000, status: "paid", on: "30 Sep 2025", receipt: "RCPT-1071" },
      { label: "On development", due: "20 Apr 2026", amt: 3280000, status: "overdue", daysLate: 54 },
      { label: "On registration", due: "10 Sep 2026", amt: 1640000, status: "due" },
    ],
    docs: [{ name: "Sale agreement", status: "signed" }, { name: "PAN + Aadhaar", status: "received" }, { name: "Allotment letter", status: "pending" }],
    futureReqs: [{ item: "Clear overdue development demand", when: "Due now" }, { item: "Plot registration", when: "10 Sep 2026" }, { item: "Khata transfer", when: "After Sep 2026" }],
    snags: [], timeline: [{ date: "01 May 2026", text: "Second reminder sent; 54 days overdue", by: "Accounts" }], service: [],
  },
];

// Deterministic sample-customer generator (stable across reloads — no RNG/clock).
function makeSampleCustomers(n, startId) {
  const FN = ["Aarav", "Vivaan", "Aditya", "Diya", "Ananya", "Kabir", "Ishaan", "Anaya", "Riya", "Arjun", "Sara", "Reyansh", "Myra", "Vihaan", "Aadhya", "Krishna", "Saanvi", "Dhruv", "Pari", "Rohan", "Neha", "Karthik", "Meera", "Suraj", "Pooja", "Aniket", "Divya", "Manish", "Sneha", "Rahul", "Kavya", "Naveen", "Anjali", "Varun", "Shruti", "Gaurav", "Tara", "Nikhil", "Isha", "Sameer", "Priya", "Aman", "Lakshmi", "Yash", "Nisha", "Harsha", "Deepa", "Vivek", "Gita", "Mohit"];
  const LN = ["Sharma", "Reddy", "Nair", "Iyer", "Shetty", "Khan", "Gupta", "Rao", "Pillai", "Menon", "Hegde", "Bhat", "Kumar", "Joshi", "Patel", "Das", "Mehta", "Naidu", "Kulkarni", "Pai", "Shenoy", "Verma", "Singh", "Acharya", "Prabhu"];
  const PROJECTS = [
    { name: "Brigade Eldorado", type: "Flat", blocks: ["A", "B", "C", "D"], base: 9000000 },
    { name: "Prestige Glenwood", type: "Villa", blocks: ["V"], base: 30000000 },
    { name: "Sunrise Layout", type: "Plot", blocks: ["P"], base: 7000000 },
    { name: "Rohan Nest", type: "Flat", blocks: ["E", "F", "G"], base: 8500000 },
    { name: "Rohan Meadows", type: "Plot", blocks: ["M"], base: 6000000 },
    { name: "Rohan Skylark", type: "Villa", blocks: ["S"], base: 26000000 },
  ];
  const SP = ["Deepak K", "Reena M", "Anita S", "Suresh P", "Farhan A"];
  const BANKS = ["SBI", "HDFC", "ICICI", "Axis", "Kotak", "Canara"];
  const FACING = ["East", "West", "North", "South", "North-East", "South-West"];
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const YEARS = ["2024", "2025", "2026"];
  const pad = (v, w) => String(v).padStart(w, "0");
  const out = [];
  for (let i = 0; i < n; i++) {
    const seed = (i + 1) * 2654435761;
    const rng = (salt) => { let h = (seed ^ (salt * 40503)) >>> 0; h = Math.imul(h ^ (h >>> 15), 2246822507); h = (h ^ (h >>> 13)) >>> 0; return h; };
    const pick = (arr, salt) => arr[rng(salt) % arr.length];
    const span = (lo, hi, salt) => lo + (rng(salt) % (hi - lo + 1));

    const id = startId + i;
    const fn = pick(FN, 1), ln = pick(LN, 2);
    const name = `${fn} ${ln}`;
    const proj = pick(PROJECTS, 3);
    const block = pick(proj.blocks, 4);
    const unit = proj.type === "Flat" ? `${block}-${pad(span(101, 1899, 5), 4)}` : proj.type === "Villa" ? `${block}-${pad(span(1, 48, 6), 2)}` : `${block}-${pad(span(1, 320, 7), 3)}`;
    const area = proj.type === "Flat" ? `${span(950, 2100, 8).toLocaleString("en-IN")} sqft` : proj.type === "Villa" ? `${span(2600, 3600, 9).toLocaleString("en-IN")} sqft` : `${[30, 30, 40][rng(10) % 3]}x${[40, 50, 60][rng(11) % 3]} sqft`;
    const salePrice = proj.base + span(0, 13, 12) * 500000;
    const year = pick(YEARS, 13);
    const sMon = rng(14) % 12, sDay = span(1, 28, 15);
    const saleDate = `${pad(sDay, 2)} ${MON[sMon]} ${year}`;
    const stageIdx = span(0, 5, 16);
    const phone = `9${span(7, 9, 17)}${span(100, 899, 18)} ${pad(span(0, 99999, 19), 5)}`;
    const email = `${fn}.${ln[0]}${id}@email.com`.toLowerCase();
    const bookingNo = `BK-${year}-${pad(id, 4)}`;

    const isPlot = proj.type === "Plot";
    const pcts = isPlot ? [10, 40, 35, 15] : [10, 20, 30, 25, 15];
    const labels = isPlot
      ? ["Booking amount", "On agreement", "On development", "On registration"]
      : ["Booking amount", "On agreement", "On foundation", "On slab casting", "On possession"];
    const paidCount = Math.min(stageIdx, labels.length);
    const overdueIdx = (paidCount < labels.length && rng(20) % 2 === 0) ? paidCount : -1;
    const schedule = labels.map((label, j) => {
      const amt = Math.round(salePrice * pcts[j] / 100);
      const due = `${pad(span(1, 28, 30 + j), 2)} ${MON[(sMon + (j + 1) * 2) % 12]} ${j < 2 ? year : (Number(year) + 1)}`;
      if (j < paidCount) return { label, due, amt, status: "paid", on: due, receipt: `RCPT-${pad(id, 4)}${j}` };
      if (j === overdueIdx) return { label, due, amt, status: "overdue", daysLate: span(5, 90, 40 + j) };
      return { label, due, amt, status: "due" };
    });

    const loan = isPlot ? null : (rng(50) % 4 === 0 ? null : { bank: pick(BANKS, 51), sanctioned: Math.round(salePrice * 0.75 / 100000) * 100000, disbursed: Math.round(salePrice * 0.75 * (paidCount / labels.length) / 100000) * 100000, status: paidCount >= labels.length ? "complete" : paidCount > 1 ? "partial" : "sanctioned" });
    const registration = stageIdx >= 2 ? { status: stageIdx > 2 ? "registered" : "scheduled", date: `${pad(span(1, 28, 60), 2)} ${MON[rng(61) % 12]} ${year}`, stampDuty: Math.round(salePrice * 0.06 / 10000) * 10000 } : null;
    const overdue = schedule.find((s) => s.status === "overdue");
    const futureReqs = [
      overdue ? { item: `Clear overdue ${overdue.label.toLowerCase()}`, when: "Due now" } : { item: "Next scheduled payment", when: "Upcoming" },
      registration && registration.status === "scheduled" ? { item: "Complete registration", when: registration.date } : { item: "Construction progress update", when: "Ongoing" },
      stageIdx >= 4 ? { item: "Possession & handover", when: "Soon" } : { item: "Documentation follow-up", when: "Pending" },
    ];
    const snags = stageIdx >= 4 && rng(70) % 3 === 0 ? [{ item: pick(["Balcony seepage", "Door alignment", "Tile chip", "Paint touch-up"], 71), status: rng(72) % 2 ? "open" : "resolved" }] : [];
    const service = stageIdx >= 4 && rng(73) % 4 === 0 ? [{ title: pick(["Plumbing fix", "Electrical check", "Carpentry", "Waterproofing"], 74), status: rng(75) % 2 ? "in-progress" : "resolved", date: saleDate }] : [];

    out.push({
      id, bookingNo, name, phone, email,
      project: proj.name, type: proj.type, unit, area, facing: pick(FACING, 80),
      salePrice, salesperson: pick(SP, 81), saleDate, stageIdx,
      loan, registration, schedule,
      docs: [{ name: "Sale agreement", status: paidCount > 1 ? "signed" : "pending" }, { name: "PAN + Aadhaar", status: "received" }],
      futureReqs, snags,
      timeline: [{ date: saleDate, text: overdue ? "Payment overdue, reminder sent" : "Booking confirmed", by: overdue ? "Accounts" : "CRM" }],
      service,
    });
  }
  return out;
}

const SEED = [...SEED_BASE, ...makeSampleCustomers(100 - SEED_BASE.length, SEED_BASE.length + 1)];

const ROLES = [
  { name: "Super Admin", access: "Top-level admin — add, create & manage anything", tone: "resolved" },
  { name: "Post Sales Manager", access: "Manage customers, collections, service & team", tone: "scheduled" },
  { name: "Post Sales Executive", access: "Day-to-day post-sales operations", tone: "due" },
  { name: "Super Viewer", access: "View everything · cannot edit or add", tone: "partial" },
];
const roleMeta = (r) => ROLES.find((x) => x.name === r) || { access: "", tone: "due" };

const SEED_USERS = [
  { name: "You (Owner)", email: "owner@rohancorporation.com", role: "Super Admin" },
  { name: "Priya Nair", email: "priya@rohancorporation.com", role: "Post Sales Manager" },
  { name: "Deepak Kumar", email: "deepak@rohancorporation.com", role: "Post Sales Executive" },
  { name: "Auditor", email: "auditor@rohancorporation.com", role: "Super Viewer" },
];

// Capability tiers per role:
//  operate = record payments, send reminders, manage tasks/service
//  manage  = create / edit / continue bookings
//  admin   = manage users & roles
const ROLE_CAPS = {
  "Super Admin": ["operate", "manage", "admin"],
  "Post Sales Manager": ["operate", "manage"],
  "Post Sales Executive": ["operate"],
  "Super Viewer": [],
};
const RoleCtx = createContext("Super Admin");
function useCan() {
  const role = useContext(RoleCtx);
  return (cap) => (ROLE_CAPS[role] || []).includes(cap);
}
const initials = (r) => { const w = String(r).trim().split(/\s+/); return ((w[0]?.[0] || "") + (w.length > 1 ? w[w.length - 1][0] : "")).toUpperCase(); };
const AUDIT = [
  { who: "Priya", what: "Recorded ₹29,25,000 payment · Fatima Khan", when: "22 Jul 2025" },
  { who: "Deepak", what: "Marked possession offered · Fatima Khan", when: "02 May 2026" },
  { who: "Priya", what: "Sent overdue reminder · Arjun Pillai", when: "12 May 2026" },
];

/* ---------------- helpers ---------------- */
function money(a) {
  const paid = a.schedule.filter((s) => s.status === "paid").reduce((x, s) => x + s.amt, 0);
  const overdue = a.schedule.filter((s) => s.status === "overdue").reduce((x, s) => x + s.amt, 0);
  const penalty = a.schedule.reduce((x, s) => x + penaltyOf(s), 0);
  return { paid, overdue, penalty, outstanding: a.salePrice - paid, pct: a.salePrice ? Math.round((paid / a.salePrice) * 100) : 0 };
}
function Pill({ tone, children }) {
  const map = {
    paid: [C.green, C.greenSoft], signed: [C.green, C.greenSoft], received: [C.green, C.greenSoft],
    resolved: [C.green, C.greenSoft], complete: [C.green, C.greenSoft], registered: [C.green, C.greenSoft],
    due: [C.muted, "#ECEBF4"], pending: [C.amber, C.amberSoft], "in-progress": [C.amber, C.amberSoft],
    open: [C.amber, C.amberSoft], scheduled: [C.amber, C.amberSoft], partial: [C.amber, C.amberSoft],
    sanctioned: [C.amber, C.amberSoft], overdue: [C.red, C.redSoft],
  };
  const [fg, bg] = map[tone] || [C.muted, "#ECEBF4"];
  return <span style={{ color: fg, background: bg, fontFamily: sans }} className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize">{children}</span>;
}
const Pbar = ({ pct }) => (
  <div className="w-full rounded-full" style={{ height: 6, background: "#E4DFD2" }}>
    <div className="rounded-full" style={{ height: 6, width: `${pct}%`, background: C.accent }} />
  </div>
);
function Card({ children, pad = 5, style }) {
  return <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 16, padding: pad * 4, ...style }}>{children}</div>;
}
function H({ children }) { return <div style={{ fontFamily: display, fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{children}</div>; }
function Switch({ on, set, disabled }) {
  return (
    <button onClick={() => !disabled && set(!on)} disabled={disabled} style={{ width: 38, height: 22, borderRadius: 99, border: "none", cursor: disabled ? "default" : "pointer", background: on ? C.accent : "#D9D3C7", opacity: disabled ? 0.5 : 1, position: "relative", flexShrink: 0 }}>
      <span style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: 99, background: "#fff", transition: "left .15s" }} />
    </button>
  );
}
const fieldStyle = (enabled) => ({ outline: "none", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 11px", fontSize: 13, background: enabled ? C.white : C.card, color: enabled ? C.ink : C.faint, fontFamily: sans, width: "100%", boxSizing: "border-box" });
function SField({ label, children }) {
  return <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</div>{children}</div>;
}

/* ---------------- UI: confirm dialog + toasts ---------------- */
const UICtx = createContext(null);
const useUI = () => useContext(UICtx) || { confirm: async (o) => window.confirm((o && o.message) || "Are you sure?"), toast: () => {} };
function UIProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [toasts, setToasts] = useState([]);
  const confirm = (opts) => new Promise((resolve) => setDialog({ ...opts, resolve }));
  const close = (val) => setDialog((d) => { if (d) d.resolve(val); return null; });
  const toast = (message, tone = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3400);
  };
  const tones = { success: [C.green, C.greenSoft, CheckCircle2], info: [C.accent, C.accentSoft, Bell], warn: [C.amber, C.amberSoft, AlertTriangle], error: [C.red, C.redSoft, AlertTriangle] };
  return (
    <UICtx.Provider value={{ confirm, toast }}>
      {children}
      {dialog && (
        <div onClick={() => close(false)} style={{ position: "fixed", inset: 0, background: "rgba(28,20,8,.42)", zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(420px,92vw)", background: C.white, borderRadius: 16, boxShadow: "0 18px 50px rgba(0,0,0,.28)", overflow: "hidden", fontFamily: sans }}>
            <div style={{ padding: "20px 22px 16px" }}>
              <div className="flex items-center gap-2.5" style={{ marginBottom: 8 }}>
                <span className="flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 10, background: dialog.danger ? C.redSoft : C.accentSoft, color: dialog.danger ? C.red : C.accent, flexShrink: 0 }}>{dialog.danger ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}</span>
                <div style={{ fontFamily: display, fontSize: 17, fontWeight: 600, color: C.ink }}>{dialog.title || "Please confirm"}</div>
              </div>
              <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.55, whiteSpace: "pre-line" }}>{dialog.message}</div>
            </div>
            <div className="flex items-center justify-end gap-2" style={{ padding: "0 22px 18px" }}>
              <button onClick={() => close(false)} style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{dialog.cancelText || "Cancel"}</button>
              <button onClick={() => close(true)} style={{ background: dialog.danger ? C.red : C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{dialog.confirmText || "Confirm"}</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ position: "fixed", top: 18, right: 18, zIndex: 3100, display: "flex", flexDirection: "column", gap: 10, pointerEvents: "none" }}>
        {toasts.map((t) => {
          const [fg, bg, Icon] = tones[t.tone] || tones.success;
          return (
            <div key={t.id} className="flex items-center gap-2.5" style={{ minWidth: 240, maxWidth: 380, background: C.white, border: `1px solid ${C.line}`, borderLeft: `4px solid ${fg}`, borderRadius: 12, padding: "12px 16px", boxShadow: "0 10px 30px rgba(0,0,0,.16)", fontFamily: sans, animation: "none" }}>
              <span className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: bg, color: fg, flexShrink: 0 }}><Icon size={16} /></span>
              <span style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{t.message}</span>
            </div>
          );
        })}
      </div>
    </UICtx.Provider>
  );
}
// Brand-styled dropdown. options: string[] or {value,label}[]. onChange(value).
function BrandSelect({ value, onChange, options, disabled, enabled = true, style, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const sel = opts.find((o) => o.value === value);
  const off = disabled || !enabled;
  return (
    <div ref={ref} style={{ position: "relative", width: "100%", ...style }}>
      <button type="button" disabled={off} onClick={() => !off && setOpen((o) => !o)}
        className="flex items-center justify-between gap-2"
        style={{ ...fieldStyle(!off), cursor: off ? "default" : "pointer", textAlign: "left", borderColor: open ? C.accent : C.line }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: sel ? C.ink : C.faint }}>{sel ? sel.label : (placeholder || "")}</span>
        <ChevronDown size={14} color={C.muted} style={{ flexShrink: 0, transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 60, background: C.white, border: `1px solid ${C.line}`, borderRadius: 9, boxShadow: "0 10px 28px rgba(110,75,30,0.18)", padding: 4 }}>
          {opts.map((o) => {
            const active = o.value === value;
            return (
              <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                style={{ padding: "8px 10px", fontSize: 13, borderRadius: 6, cursor: "pointer", background: active ? C.accent : "transparent", color: active ? "#fff" : C.ink, fontWeight: active ? 600 : 400 }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.accentSoft; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
// Parse applicant fields out of OCR text from an Aadhaar / PAN scan.
function parseIdText(raw) {
  const text = (raw || "").replace(/\r/g, "");
  const lines = text.split(/\n/).map((s) => s.trim()).filter(Boolean);
  const out = {};

  const aadhaar = text.match(/\b(\d{4}\s\d{4}\s\d{4})\b/);
  if (aadhaar) { out.p_aadhaar = aadhaar[1]; out.p_nationality = "Indian"; }

  const pan = text.match(/\b([A-Z]{5}[0-9]{4}[A-Z])\b/);
  if (pan) out.p_pan = pan[1];

  const dob = text.match(/(\d{2}[/\-.]\d{2}[/\-.]\d{4})/);
  if (dob) {
    out.p_dob = dob[1].replace(/[\-.]/g, "/");
    const yr = parseInt(out.p_dob.slice(-4), 10);
    if (yr > 1900) out.p_age = String(Math.max(0, new Date().getFullYear() - yr));
  }

  const gender = text.match(/\b(MALE|FEMALE|Male|Female|TRANSGENDER|Transgender)\b/);
  if (gender) out.p_gender = gender[1][0].toUpperCase() + gender[1].slice(1).toLowerCase();

  // 6-digit PIN that is not part of the 12-digit Aadhaar block.
  const pin = (text.replace(/\d{4}\s\d{4}\s\d{4}/g, "")).match(/\b([1-9]\d{5})\b/);
  if (pin) out.p_pin = pin[1];

  const STATES = ["Karnataka", "Maharashtra", "Tamil Nadu", "Kerala", "Telangana", "Andhra Pradesh", "Delhi", "Gujarat", "Rajasthan", "Uttar Pradesh", "West Bengal", "Punjab", "Haryana", "Madhya Pradesh", "Bihar", "Odisha", "Goa"];
  const st = STATES.find((s) => text.toLowerCase().includes(s.toLowerCase()));
  if (st) out.p_state = st;

  // Name: PAN has a "Name" label; Aadhaar has the name on the line above DOB.
  let name;
  const ni = lines.findIndex((l) => /^name\b/i.test(l));
  if (ni >= 0) name = /^name[:\s]+(.+)/i.test(lines[ni]) ? lines[ni].replace(/^name[:\s]+/i, "") : lines[ni + 1];
  if (!name) {
    const di = lines.findIndex((l) => /DOB|Date of Birth|Year of Birth/i.test(l));
    for (let j = di - 1; j >= 0 && di > 0; j--) {
      const l = lines[j];
      if (/^[A-Za-z][A-Za-z .]{2,}$/.test(l) && l.split(/\s+/).length >= 2 && !/government|india|male|female|republic/i.test(l)) { name = l; break; }
    }
  }
  if (name) out.p_name = name.replace(/\s+/g, " ").trim();

  // Address: Aadhaar has an "Address" block ending around the 6-digit PIN.
  const ai = lines.findIndex((l) => /address/i.test(l));
  if (ai >= 0) {
    let addr = lines[ai].replace(/^.*address[:\s-]*/i, "");
    for (let j = ai + 1; j < lines.length && j < ai + 6; j++) {
      addr += " " + lines[j];
      if (/\b\d{6}\b/.test(lines[j])) break;
    }
    addr = addr.replace(/\s+/g, " ").trim();
    if (addr.length > 8) out.p_curaddr = addr;
  }

  return out;
}

const TASK_SEED = [
  { id: 1, text: "Call Ananya re: foundation dues", done: false, who: "Priya" },
  { id: 2, text: "Schedule Vikram registration paperwork", done: false, who: "Deepak" },
  { id: 3, text: "Close Lakshmi balcony seepage ticket", done: false, who: "Deepak" },
  { id: 4, text: "Send Fatima Khata transfer update", done: true, who: "Deepak" },
];

/* ---------------- app ---------------- */
export default function App() {
  const [data, setData] = useState(SEED);
  const [plans, setPlans] = useState(MILESTONE_SEED);

  // Load customers from the database on mount (seeds it on first run).
  useEffect(() => {
    if (!dbEnabled) return;
    let alive = true;
    (async () => {
      let rows = await loadCustomers();
      if (rows && rows.length === 0) { await seedCustomers(SEED); rows = SEED; }
      if (alive && rows) setData(rows);
    })();
    return () => { alive = false; };
  }, []);

  // Load milestone plans from the database on mount (seeds it on first run).
  useEffect(() => {
    if (!dbEnabled) return;
    let alive = true;
    (async () => {
      let rows = await loadPlans();
      if (rows && rows.length === 0) { await seedPlans(MILESTONE_SEED); rows = MILESTONE_SEED; }
      if (alive && rows) setPlans(rows);
    })();
    return () => { alive = false; };
  }, []);

  const savePlanRec = (plan) => {
    setPlans((ps) => ps.some((p) => p.id === plan.id) ? ps.map((p) => p.id === plan.id ? plan : p) : [...ps, plan]);
    savePlan(plan);
  };
  const removePlanRec = (id) => { setPlans((ps) => ps.filter((p) => p.id !== id)); deletePlan(id); };
  // Super-admin marks a plan milestone finished → raise the matching demand on every buyer of that project
  // (unpaid "due" instalment becomes collectable/"overdue"). Un-marking withdraws only demands this raised.
  const syncMilestoneDemand = (plan, milestone, done) => {
    if (!plan || !milestone) return;
    const label = milestone.label, stage = milestone.stage;
    const updates = [];
    data.forEach((a) => {
      const onPlan = (a.planId != null && a.planId === plan.id) || (a.project && plan.name && a.project.trim().toLowerCase() === plan.name.trim().toLowerCase());
      if (!onPlan) return;
      let touched = false;
      const schedule = a.schedule.map((s) => {
        if (s.label !== label || (stage && s.stage && s.stage !== stage)) return s;
        if (done && s.status === "due") { touched = true; return { ...s, status: "overdue", demanded: true }; }
        if (!done && s.demanded && s.status === "overdue") { touched = true; const { demanded, ...rest } = s; return { ...rest, status: "due" }; }
        return s;
      });
      if (touched) updates.push({ ...a, schedule, timeline: [{ date: "today", text: done ? `Demand raised — milestone "${label}" achieved` : `Demand withdrawn — milestone "${label}"`, by: "Accounts" }, ...a.timeline] });
    });
    if (!updates.length) return;
    const byId = new Map(updates.map((u) => [u.id, u]));
    setData((d) => d.map((x) => byId.get(x.id) || x));
    updates.forEach(saveCustomer);
  };

  const [view, setView] = useState(() => localStorage.getItem("ps_view") || "Dashboard");
  const [openId, setOpenId] = useState(() => { const v = localStorage.getItem("ps_openId"); return v ? Number(v) : null; });
  const [editId, setEditId] = useState(null);
  const [draftForm, setDraftForm] = useState(null);
  const [loggedOut, setLoggedOut] = useState(false);
  const [role, setRole] = useState("Super Admin");
  const canManage = (ROLE_CAPS[role] || []).includes("manage");

  // Remember the open section / account across refreshes.
  useEffect(() => { localStorage.setItem("ps_view", view); }, [view]);
  useEffect(() => {
    if (openId == null) localStorage.removeItem("ps_openId");
    else localStorage.setItem("ps_openId", String(openId));
  }, [openId]);
  const [tasks, setTasks] = useState(TASK_SEED);

  // Load tasks from the database on mount (seeds it on first run).
  useEffect(() => {
    if (!dbEnabled) return;
    let alive = true;
    (async () => {
      let rows = await loadTasks();
      if (rows && rows.length === 0) { await seedTasks(TASK_SEED); rows = TASK_SEED; }
      if (alive && rows) setTasks(rows);
    })();
    return () => { alive = false; };
  }, []);

  const addTask = (task) => { setTasks((t) => [...t, task]); saveTask(task); };
  const toggleTask = (id) => setTasks((t) => {
    const next = t.map((i) => i.id === id ? { ...i, done: !i.done } : i);
    const changed = next.find((i) => i.id === id);
    if (changed) saveTask(changed);
    return next;
  });
  const deleteTaskRec = (id) => { setTasks((t) => t.filter((i) => i.id !== id)); deleteTask(id); };

  // Realtime: refresh local state whenever any client changes a table.
  useEffect(() => {
    if (!dbEnabled) return;
    const unsubs = [
      subscribe("customers", async () => { const r = await loadCustomers(); if (r) setData(r); }),
      subscribe("plans", async () => { const r = await loadPlans(); if (r) setPlans(r); }),
      subscribe("tasks", async () => { const r = await loadTasks(); if (r) setTasks(r); }),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  const pay = (accId, idx) => {
    const a = data.find((x) => x.id === accId);
    if (!a) return;
    const updated = {
      ...a,
      schedule: a.schedule.map((s, i) => i === idx ? { ...s, status: "paid", on: "today", receipt: "RCPT-" + (1300 + Math.floor(Math.random() * 99)) } : s),
      timeline: [{ date: "today", text: `Payment recorded: ${a.schedule[idx].label} (${inr(a.schedule[idx].amt)})`, by: "Accounts" }, ...a.timeline],
    };
    setData((d) => d.map((x) => x.id === accId ? updated : x));
    saveCustomer(updated);
  };
  const remind = (accId, idx) => {
    const a = data.find((x) => x.id === accId);
    if (!a) return;
    const updated = {
      ...a,
      schedule: a.schedule.map((s, i) => i === idx ? { ...s, reminded: true } : s),
      timeline: [{ date: "today", text: `Reminder sent (SMS + WhatsApp): ${a.schedule[idx].label}`, by: "Accounts" }, ...a.timeline],
    };
    setData((d) => d.map((x) => x.id === accId ? updated : x));
    saveCustomer(updated);
  };
  const updateAcc = (id, patch) => {
    const a = data.find((x) => x.id === id);
    if (!a) return;
    const updated = { ...a, ...patch };
    setData((d) => d.map((x) => x.id === id ? updated : x));
    saveCustomer(updated);
  };

  const buildRecord = (f, id, bookingNo, draft) => {
    const amt = Number(f.finalSaleValue) || 0;
    const booking = Number(f.bookingAmount) || Math.round(amt * 0.1);
    const agreement = Math.round(amt * 0.1);
    const type = (f.config === "Plot" || f.projectType === "Plotted") ? "Plot" : f.projectType === "Commercial" ? "Commercial" : "Flat";
    // Auto-match a milestone plan by project name; scale each % to this buyer's sale value.
    const plan = plans.find((p) => p.name && f.project && p.name.trim().toLowerCase() === f.project.trim().toLowerCase());
    const planSchedule = plan ? plan.milestones.map((mItem, i) => ({
      label: mItem.label, stage: mItem.stage, pct: mItem.pct, due: "as per plan",
      amt: Math.round(amt * mItem.pct / 100),
      status: i === 0 && booking ? "paid" : "due",
      on: i === 0 && booking ? "today" : undefined,
      receipt: i === 0 && booking ? "RCPT-NEW" : undefined,
    })) : null;
    return {
      id, bookingNo, draft, planId: plan ? plan.id : null,
      name: f.p_name || "Untitled draft", phone: f.p_phone || "", email: f.p_email || "",
      project: f.project || "—", type, unit: f.unitNo || "—",
      area: f.sba ? f.sba + " sqft" : "—", facing: f.facing || "—",
      salePrice: amt, salesperson: f.salesExec || "—", saleDate: "today", stageIdx: 0,
      loan: (f.funding && f.funding !== "Self-Funded") ? { bank: f.loanBank || "—", sanctioned: Number(f.loanAmount) || 0, disbursed: 0, status: (f.loanStatus || "applied").toLowerCase() } : null,
      registration: null,
      schedule: draft ? [] : planSchedule ? planSchedule : [
        { label: "Booking amount", due: "today", amt: booking, status: booking ? "paid" : "due", on: booking ? "today" : undefined, receipt: booking ? "RCPT-NEW" : undefined },
        { label: "On agreement", due: "on signing", amt: agreement, status: "due" },
        { label: "Balance", due: "as per plan", amt: Math.max(0, amt - booking - agreement), status: "due" },
      ],
      docs: ((f.kyc && f.kyc.length) ? f.kyc : ["Aadhaar", "PAN"]).map((n) => ({ name: n, status: "received" })),
      survey: { heard: f.heard || [], finalised: f.finalised || [], notes: f.insightNotes || "" },
      snags: [], service: [],
      timeline: [{ date: "today", text: draft ? "Draft saved" : "Booking created from booking form", by: f.salesExec || "You" }],
      form: f,
    };
  };
  const saveBooking = (f, draft) => {
    if (editId != null) {
      const existing = data.find((a) => a.id === editId);
      const record = buildRecord(f, editId, existing ? existing.bookingNo : "BK-2026-" + (1000 + editId), draft);
      setData((d) => d.map((a) => a.id === editId ? record : a));
      saveCustomer(record);
    } else {
      const id = Math.max(0, ...data.map((a) => a.id)) + 1;
      const record = buildRecord(f, id, "BK-2026-" + (1000 + id), draft);
      setData((d) => [record, ...d]);
      saveCustomer(record);
    }
    setEditId(null); setDraftForm(null); setView("Customers");
  };
  const editBooking = (id) => {
    const a = data.find((x) => x.id === id);
    setDraftForm(a && a.form ? a.form : {});
    setEditId(id); setOpenId(null); setView("New booking");
  };

  const NAV = [
    ["Dashboard", LayoutDashboard], ["Ask AI", Sparkles], ["New booking", UserPlus], ["Customers", Users], ["Collections", Wallet],
    ["Milestones", Landmark], ["Tasks", ListChecks], ["Reports", BarChart3],
    ["Customer portal", Smartphone], ["Settings", Settings],
  ].filter(([label]) => label !== "New booking" || canManage);

  const go = (v) => { setView(v); setOpenId(null); setEditId(null); setDraftForm(null); };

  if (loggedOut) {
    return (
      <div className="flex items-center justify-center" style={{ background: C.paper, color: C.ink, fontFamily: sans, minHeight: "calc(100vh / var(--app-zoom))" }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600&family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap');`}</style>
        <div className="text-center" style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 18, padding: "40px 44px" }}>
          <img src={LOGO} onError={(e) => { if (e.currentTarget.src !== _OLD_LOGO) e.currentTarget.src = _OLD_LOGO; }} alt="Rohan Corporation" style={{ height: 64, width: "auto", margin: "0 auto 18px", display: "block", borderRadius: 6 }} />
          <div style={{ fontFamily: display, fontSize: 22, fontWeight: 600 }}>You've been signed out</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Thanks for using the Post-Sale Desk.</div>
          <button onClick={() => { setLoggedOut(false); setView("Dashboard"); }} className="flex items-center gap-1.5" style={{ margin: "22px auto 0", background: C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Sign back in</button>
        </div>
      </div>
    );
  }

  return (
    <UIProvider>
    <RoleCtx.Provider value={role}>
    <div className="flex" style={{ background: C.paper, color: C.ink, fontFamily: sans, height: "calc(100vh / var(--app-zoom))", overflow: "hidden" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600&family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap');`}</style>

      {/* sidebar */}
      <div style={{ width: 210, background: C.white, color: C.muted, flexShrink: 0, borderRight: `1px solid ${C.line}` }} className="flex flex-col">
        <div className="flex items-center gap-2.5 px-4 py-4" style={{ borderBottom: `1px solid ${C.line}` }}>
          <img src={LOGO} onError={(e) => { if (e.currentTarget.src !== _OLD_LOGO) e.currentTarget.src = _OLD_LOGO; }} alt="Rohan Corporation" style={{ height: 44, width: "auto", borderRadius: 6, display: "block" }} />
          <div>
            <div style={{ fontFamily: display, fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1 }}>Post-Sale Desk</div>
            <div style={{ fontSize: 10, color: C.faint }}>Rohan Corporation</div>
          </div>
        </div>
        <div className="py-3 px-2">
          {NAV.map(([label, Icon]) => {
            const active = view === label;
            return (
              <button key={label} onClick={() => go(label)}
                className="flex items-center gap-2.5 w-full px-3 py-2.5" style={{
                  background: active ? "linear-gradient(90deg,#6E4B1E,#8A6326)" : "transparent", border: "none", cursor: "pointer", borderRadius: 10, marginBottom: 2,
                  color: active ? "#fff" : C.muted, fontFamily: sans, fontSize: 16.5, fontWeight: active ? 600 : 400,
                  boxShadow: active ? "0 1px 4px rgba(110,75,30,0.25)" : "none",
                }}>
                <Icon size={18} /> {label}
              </button>
            );
          })}
        </div>
        <div className="mt-auto px-2 pb-3">
          <div style={{ borderTop: `1px solid ${C.line}`, margin: "0 6px 8px" }} />
          <div className="flex items-center gap-1" style={{ flexWrap: "nowrap" }}>
            <button onClick={() => go("Settings")} className="flex items-center gap-2.5 px-2 py-2" style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", cursor: "pointer", borderRadius: 10, textAlign: "left" }}>
              <div className="flex items-center justify-center rounded-full" style={{ width: 34, height: 34, background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{initials(role)}</div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, lineHeight: 1.15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{role}</div>
                <div style={{ fontSize: 10.5, color: C.faint }}>View profile</div>
              </div>
            </button>
            <button onClick={() => setLoggedOut(true)} title="Log out" className="flex items-center justify-center" style={{ flexShrink: 0, background: "transparent", border: "none", cursor: "pointer", borderRadius: 10, color: C.red, padding: 8 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.redSoft)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* main */}
      <div className="flex-1 flex flex-col" style={{ minWidth: 0 }}>
        <div className="flex items-center justify-between px-6 py-3" style={{ borderBottom: `1px solid ${C.line}`, background: C.white, flexShrink: 0 }}>
          <div style={{ fontFamily: display, fontSize: 18, fontWeight: 600 }}>{openId != null && data.find((a) => a.id === openId) ? "Account file" : view}</div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-full px-3" style={{ background: C.card, border: `1px solid ${C.line}`, height: 36 }}>
              <Search size={15} color={C.faint} />
              <input placeholder="Search anything…" style={{ outline: "none", border: "none", background: "transparent", fontSize: 13, width: 150, color: C.ink }} />
            </div>
            {canManage && <button onClick={() => go("New booking")} className="flex items-center gap-1.5 rounded-full" style={{ background: C.accent, color: "#fff", border: "none", height: 36, padding: "0 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Plus size={15} /> New booking</button>}
            <Bell size={18} color={C.muted} />
          </div>
        </div>
        <div className="px-6 py-5 no-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {openId != null && data.find((a) => a.id === openId) ? <Detail acc={data.find((a) => a.id === openId)} onBack={() => setOpenId(null)} onPay={pay} onUpdate={updateAcc} />
            : view === "Dashboard" ? <Dashboard data={data} tasks={tasks} open={(id) => setOpenId(id)} go={go} />
            : view === "Ask AI" ? <AskAI />
            : view === "New booking" ? (canManage ? <NewBooking key={editId == null ? "new" : "edit-" + editId} initial={draftForm} editing={editId != null} onSave={saveBooking} plans={plans} /> : <NoAccess />)
            : view === "Customers" ? <Accounts data={data} open={(id) => setOpenId(id)} onEdit={editBooking} />
            : view === "Collections" ? <Collections data={data} plans={plans} onPay={pay} onRemind={remind} />
            : view === "Service" ? <Service data={data} open={(id) => setOpenId(id)} />
            : view === "Milestones" ? <Milestones plans={plans} onSave={savePlanRec} onDelete={removePlanRec} onMilestoneDone={syncMilestoneDemand} />
            : view === "Tasks" ? <Tasks tasks={tasks} onAdd={addTask} onToggle={toggleTask} />
            : view === "Reports" ? <Reports data={data} />
            : view === "Customer portal" ? <Portal acc={data[0]} />
            : <SettingsView role={role} setRole={setRole} />}
        </div>
      </div>
    </div>
    </RoleCtx.Provider>
    </UIProvider>
  );
}
function NoAccess() {
  return (
    <Card style={{ textAlign: "center", padding: 48 }}>
      <div style={{ fontFamily: display, fontSize: 18, fontWeight: 600 }}>No access</div>
      <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>Your role doesn't have permission to view this. Switch role in Settings.</div>
    </Card>
  );
}

/* ---------------- dashboard ---------------- */
function Dashboard({ data, tasks, open, go }) {
  const live = data.filter((a) => !a.draft);
  const agg = useMemo(() => {
    let outstanding = 0, overdue = 0, possession = 0, tickets = 0, collected = 0;
    live.forEach((a) => { const m = money(a); outstanding += m.outstanding; collected += m.paid; if (m.overdue) overdue++; if (a.stageIdx === 4) possession++; tickets += a.service.filter((s) => s.status !== "resolved").length; });
    return { outstanding, overdue, possession, tickets, collected };
  }, [data]);
  const overdueList = live.filter((a) => money(a).overdue > 0);

  const months = [
    { m: "Jan", c: 18, t: 26 }, { m: "Feb", c: 24, t: 28 }, { m: "Mar", c: 31, t: 33 }, { m: "Apr", c: 22, t: 30 },
    { m: "May", c: 38, t: 36 }, { m: "Jun", c: 29, t: 34 }, { m: "Jul", c: 35, t: 38 }, { m: "Aug", c: 41, t: 40 },
    { m: "Sep", c: 27, t: 35 }, { m: "Oct", c: 33, t: 37 }, { m: "Nov", c: 44, t: 42 }, { m: "Dec", c: 39, t: 41 },
  ];
  let run = 0; const trend = months.map((x) => ({ m: x.m, v: (run += x.c) }));
  const stageData = STAGES.map((s, i) => ({ name: s, value: live.filter((a) => a.stageIdx === i).length })).filter((d) => d.value > 0);
  const tip = { background: C.white, border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 12, fontFamily: sans };

  return (
    <div>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(4,minmax(0,1fr))" }}>
        <Kpi label="Total outstanding" value={inr(agg.outstanding)} sub="across active accounts" icon={Wallet} delta={{ dir: "up", v: "2.4%" }} onClick={() => go("Collections")} highlight />
        <Kpi label="Accounts overdue" value={agg.overdue} sub="need follow-up" tone={C.red} icon={AlertTriangle} delta={{ dir: "down", v: "0.6%" }} onClick={() => go("Collections")} />
        <Kpi label="Possession pending" value={agg.possession} sub="ready to hand over" tone={C.amber} icon={ClipboardCheck} />
        <Kpi label="Open service requests" value={agg.tickets} sub="post-handover" icon={Wrench} onClick={() => go("Service")} />
      </div>

      <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1.55fr 1fr" }}>
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <H>Collections overview</H>
              <div className="flex items-center gap-2" style={{ marginTop: -6 }}>
                <span style={{ fontFamily: display, fontSize: 26, fontWeight: 600 }}>{inr(agg.collected)}</span>
                <span className="rounded-full px-2 py-0.5" style={{ background: C.greenSoft, color: C.green, fontSize: 11, fontWeight: 600 }}>▲ 8.4%</span>
              </div>
            </div>
            <div className="flex items-center gap-3" style={{ fontSize: 12, color: C.muted }}>
              <span className="flex items-center gap-1.5"><span style={{ width: 9, height: 9, borderRadius: 2, background: C.accent }} /> Collected</span>
              <span className="flex items-center gap-1.5"><span style={{ width: 9, height: 9, borderRadius: 2, background: "#E6D8BC" }} /> Target</span>
            </div>
          </div>
          <div style={{ height: 230, marginTop: 8 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} barGap={3} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: C.faint, fontFamily: sans }} />
                <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} contentStyle={tip} formatter={(v) => [`₹${v} L`, ""]} />
                <Bar dataKey="t" radius={[4, 4, 0, 0]} fill="#E6D8BC" />
                <Bar dataKey="c" radius={[4, 4, 0, 0]}>
                  {months.map((x, i) => <Cell key={i} fill={i === 7 ? C.lime : C.accent} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <H>Portfolio by stage</H>
          <div style={{ height: 200, position: "relative" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stageData} dataKey="value" nameKey="name" innerRadius={58} outerRadius={88} paddingAngle={2} stroke="none">
                  {stageData.map((e, i) => <Cell key={i} fill={PIE[STAGES.indexOf(e.name)]} />)}
                </Pie>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <div style={{ fontFamily: display, fontSize: 28, fontWeight: 600 }}>{live.length}</div>
              <div style={{ fontSize: 11, color: C.faint }}>accounts</div>
            </div>
          </div>
          <div className="grid gap-1.5 mt-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
            {stageData.map((e) => (
              <div key={e.name} className="flex items-center gap-1.5" style={{ fontSize: 11, color: C.muted }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: PIE[STAGES.indexOf(e.name)] }} /> {e.name} ({e.value})
              </div>))}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: "1.55fr 1fr" }}>
        <Card>
          <div className="flex items-start justify-between">
            <H>Collections trend</H>
            <span className="rounded-full px-2 py-0.5" style={{ background: C.greenSoft, color: C.green, fontSize: 11, fontWeight: 600 }}>▲ 9.4%</span>
          </div>
          <div style={{ height: 190 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.lime} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={C.lime} stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: C.faint, fontFamily: sans }} />
                <Tooltip contentStyle={tip} formatter={(v) => [`₹${v} L`, "cumulative"]} />
                <Area type="monotone" dataKey="v" stroke={C.accent} strokeWidth={2.5} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <H>Overdue collections</H>
          {overdueList.map((a) => { const m = money(a); return (
            <div key={a.id} onClick={() => open(a.id)} className="flex items-center justify-between py-2 cursor-pointer" style={{ borderBottom: `1px solid ${C.line}` }}>
              <div><div style={{ fontSize: 13, fontWeight: 500 }}>{a.name}</div><div style={{ fontSize: 11, color: C.faint }}>{a.unit}</div></div>
              <div className="text-right"><div style={{ fontFamily: display, fontWeight: 600 }}>{inr(m.overdue)}</div><Pill tone="overdue">+{inr(m.penalty)} penalty</Pill></div>
            </div>); })}
          <div className="mt-2" style={{ fontSize: 11, color: C.faint, fontWeight: 600 }}>OPEN TASKS</div>
          {tasks.filter((t) => !t.done).slice(0, 3).map((t) => (
            <div key={t.id} className="flex items-center gap-2 py-1.5">
              <span style={{ width: 6, height: 6, borderRadius: 99, background: C.lime }} />
              <div style={{ fontSize: 12.5 }}>{t.text}</div>
            </div>))}
        </Card>
      </div>
    </div>
  );
}
function Kpi({ label, value, sub, tone, icon: Icon, delta, onClick, highlight }) {
  const hl = highlight;
  return (
    <div onClick={onClick} style={{ background: hl ? C.accentSoft : C.white, border: `1px solid ${hl ? C.accent : C.line}`, borderRadius: 16, padding: 18, cursor: onClick ? "pointer" : "default" }}>
      <div className="flex items-start justify-between">
        <div style={{ fontSize: 12.5, color: C.muted }}>{label}</div>
        {Icon && <div className="flex items-center justify-center rounded-full" style={{ width: 32, height: 32, background: hl ? C.white : C.accentSoft }}><Icon size={16} color={C.accent} /></div>}
      </div>
      <div style={{ fontFamily: display, fontSize: 27, fontWeight: 600, color: hl ? C.accent : (tone || C.ink), lineHeight: 1.25, marginTop: 6 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: C.faint, marginTop: 2 }}>
        {delta && <span style={{ color: delta.dir === "up" ? C.green : C.red, fontWeight: 600 }}>{delta.dir === "up" ? "▲" : "▼"} {delta.v} </span>}{sub}
      </div>
    </div>
  );
}

/* ---------------- accounts ---------------- */
const AV = ["#6E4B1E", "#8A6326", "#A98445", "#3B7A4E", "#B0780F", "#5B6E8C", "#8C5B6E"];
function avColor(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AV[h % AV.length]; }
function Avatar({ name }) {
  const c = avColor(name || "?");
  return <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 11, background: c + "1A", color: c, fontFamily: sans, fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>{initials(name || "?") || "?"}</span>;
}
const COLS = "2fr 1.6fr 1.1fr 1.5fr 1fr";
function Accounts({ data, open, onEdit }) {
  const [q, setQ] = useState(""); const [filter, setFilter] = useState("All");
  const can = useCan();
  const list = data.filter((a) => {
    const hit = (a.name + a.bookingNo + a.project + a.unit).toLowerCase().includes(q.toLowerCase());
    const fOk = filter === "All" ? true : filter === "Drafts" ? a.draft : (!a.draft && STAGES[a.stageIdx] === filter);
    return hit && fOk;
  });
  const live = data.filter((a) => !a.draft);
  const totColl = live.reduce((x, a) => x + money(a).paid, 0);
  const totOut = live.reduce((x, a) => x + money(a).outstanding, 0);
  const nOverdue = live.filter((a) => money(a).overdue > 0).length;
  const counts = (s) => s === "All" ? data.length : s === "Drafts" ? data.filter((a) => a.draft).length : data.filter((a) => !a.draft && STAGES[a.stageIdx] === s).length;

  return (
    <div>
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        <Kpi label="Customers" value={live.length} sub={`${data.length - live.length} draft${data.length - live.length === 1 ? "" : "s"}`} icon={Users} />
        <Kpi label="Collected" value={inr(totColl)} sub="across active bookings" tone={C.green} icon={Wallet} />
        <Kpi label="Outstanding" value={inr(totOut)} sub="yet to be received" tone={C.accent} icon={Receipt} />
        <Kpi label="Overdue" value={nOverdue} sub={nOverdue ? "need follow-up" : "all on track"} tone={nOverdue ? C.red : C.green} icon={AlertTriangle} />
      </div>

      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 rounded-xl px-3" style={{ background: C.white, border: `1px solid ${C.line}`, height: 40, minWidth: 280 }}>
          <Search size={16} color={C.faint} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, booking no., unit" style={{ outline: "none", border: "none", background: "transparent", fontSize: 13, flex: 1, color: C.ink }} />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {["All", "Drafts", ...STAGES].map((s) => { const on = filter === s; const n = counts(s); return (
            <button key={s} onClick={() => setFilter(s)} className="inline-flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, height: 32, padding: "0 13px", borderRadius: 99, cursor: "pointer", transition: "all .12s", background: on ? C.accent : C.white, color: on ? "#fff" : C.muted, border: `1px solid ${on ? C.accent : C.line}` }}>
              {s}<span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 6px", borderRadius: 99, background: on ? "rgba(255,255,255,.22)" : C.card, color: on ? "#fff" : C.faint }}>{n}</span>
            </button>
          ); })}
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.line}`, background: C.white }}>
        <div className="grid items-center px-5 py-3" style={{ gridTemplateColumns: COLS, background: C.card, borderBottom: `1px solid ${C.line}`, fontSize: 10.5, color: C.muted, fontWeight: 700, letterSpacing: 0.6 }}>
          <div>CUSTOMER</div><div>UNIT</div><div>STAGE</div><div>COLLECTED</div><div className="text-right">OUTSTANDING</div>
        </div>
        {list.length === 0 && (
          <div className="flex flex-col items-center justify-center" style={{ padding: "56px 0", color: C.faint }}>
            <Search size={28} color={C.faint} />
            <div style={{ fontSize: 14, fontWeight: 600, color: C.muted, marginTop: 12 }}>No customers found</div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>Try a different search or filter.</div>
          </div>
        )}
        {list.map((a, i) => { const m = money(a); const draft = a.draft; const last = i === list.length - 1; return (
          <div key={a.id} onClick={() => draft ? (can("manage") && onEdit(a.id)) : open(a.id)} className="grid items-center px-5 cursor-pointer" style={{ gridTemplateColumns: COLS, background: C.white, borderBottom: last ? "none" : `1px solid ${C.line}`, paddingTop: 13, paddingBottom: 13, transition: "background .12s" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.paper)} onMouseLeave={(e) => (e.currentTarget.style.background = C.white)}>
            <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
              <Avatar name={a.name} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
                <div style={{ fontSize: 11.5, color: C.faint, marginTop: 1 }}><span style={{ fontFamily: "ui-monospace, monospace", color: C.accent }}>{a.bookingNo || "draft"}</span> · {a.salesperson}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, minWidth: 0 }}><div style={{ color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.project}</div><div style={{ fontSize: 11.5, color: C.faint, marginTop: 1 }}>{a.type} · {a.unit}</div></div>
            <div>{draft ? <Pill tone="pending">Draft</Pill> : <Pill tone={a.stageIdx >= 5 ? "resolved" : "due"}>{STAGES[a.stageIdx]}</Pill>}</div>
            {draft ? (<>
              <div className="pr-4" style={{ fontSize: 12, color: C.faint }}>Not committed</div>
              <div className="text-right inline-flex items-center justify-end gap-1" style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>Continue <ChevronRight size={14} /></div>
            </>) : (<>
              <div className="pr-5">
                <div className="flex items-center justify-between mb-1.5" style={{ fontSize: 11.5 }}>
                  <span style={{ color: m.pct >= 100 ? C.green : C.muted, fontWeight: 600 }}>{m.pct}%</span>
                  {m.overdue > 0 && <span className="inline-flex items-center gap-1" style={{ color: C.red, fontWeight: 600 }}><AlertTriangle size={11} />overdue</span>}
                </div>
                <Pbar pct={m.pct} />
              </div>
              <div className="text-right" style={{ fontFamily: display, fontSize: 15.5, fontWeight: 600, color: m.outstanding > 0 ? C.ink : C.green }}>{inr(m.outstanding)}</div>
            </>)}
          </div>); })}
      </div>
    </div>
  );
}

/* ---------------- new booking (full booking form) ---------------- */
const GENDER = ["Male", "Female", "Other"];
const MARITAL = ["Single", "Married", "Other"];
const CUSTTYPE = ["Local", "NRI", "HNI"];
const EMPTYPE = ["Salaried", "Self-Employed", "Business Owner", "Freelancer", "Retired", "NRI", "Student"];
const PROJTYPE = ["Residential", "Commercial", "Plotted"];
const CONFIG = ["1BHK", "2BHK", "3BHK", "Plot", "Other"];
const FACING = ["Sea", "Road", "Garden", "Other"];
const PURPOSE = ["Self Use", "Investment", "Rental", "Gifting"];
const MODE = ["Cheque", "DD", "RTGS", "NEFT", "IMPS", "UPI"];
const FUNDING = ["Self-Funded", "Home Loan", "Combination (Self + Loan)"];
const LOANSTATUS = ["Pre-approved", "Applied", "Sanctioned", "Pending", "Not Required"];
const YESNO = ["Yes", "No"];
const FLOORPREF = ["Low", "Mid", "High", "No Preference"];
const COMM = ["Phone", "WhatsApp", "Email", "SMS"];
const LANG = ["English", "Kannada", "Hindi", "Tulu", "Konkani", "Other"];
const BESTTIME = ["Morning (9 AM-12 PM)", "Afternoon (12-4 PM)", "Evening (4-8 PM)"];
const PAYPLAN = ["Full", "CLP", "Time-Linked", "Subvention", "Other"];
const LEADSRC = ["Meta", "Google", "Walk-in", "WhatsApp", "Referral", "Newspaper", "Hoarding", "Event", "Other"];
const REWARD = ["Pending", "Approved", "Disbursed"];
const DOCS = ["Aadhaar", "PAN", "Photo", "Address proof", "GPA copy", "Booking receipt"];
const BRAND = "Rohan Corporation";
const COMPANY = {
  legal: "Rohan Corporation India Pvt.Ltd",
  former: "(Formerly known as M/s. Property Infra Tech India Pvt Ltd).",
  cin: "U45400KA2010PTC052992",
  regOffice: "3-T-19/6(1), Rohan Corporation, Near Capitanio, Pumpwell, Mangalore, D.K -575002.",
  tel: "0824-2431510",
  bank: { beneficiary: "ROHAN CORPORATION INDIA PVT LTD RERA A/C ROHAN CITY", name: "KARNATAKA BANK LTD", acc: "4752000100159301", ifsc: "KARB0000475", branch: "FOUNDERS BRANCH, DONGERKERY MANGALORE" },
};
const HEARD = ["Walk-in", "Google search", "Meta / Instagram", "WhatsApp", "Referral (friend/family)", "Existing customer", "Newspaper", "Hoarding / banner", "Property portal", "Event / expo", "Other"];
const FINALISED = ["Location", "Price / value for money", "Builder reputation", "Construction quality", "Amenities", "Layout / Vastu", "Connectivity", "Investment potential", "Loan / payment plan", "Trusted referral", "Other"];
// General Terms & Conditions — verbatim from the printed Rohan Corporation booking form.
const TERMS = [
  "The Applicant has applied for the booking of a Unit by paying the booking amount after reading and understanding the terms and conditions of this Application. Subscribing to this Application amounts to deemed acceptance of all terms and conditions specified herein.",
  "The Applicant cannot claim any right or interest in the Unit merely by subscribing to this Booking Form. Booking confirmation of the Unit is entirely at the discretion of M/s. Rohan Corporation India Pvt. Ltd. (the Company).",
  "Self-attested proof of address (i.e. Aadhaar card, Passport copy and PAN card copy) and passport-size photograph need to be attached along with this form. If the booking is in joint name, both applicants need to sign this form and submit all necessary documents.",
  "The Applicant(s) has inspected the location of the project and, having been satisfied with the location and the explanation of project, have signed and submitted this form to Rohan Corporation India Pvt. Ltd. (the Company) for booking the Unit. The Applicant has also read and understood the details and specifications of the project mentioned in the brochure / details provided by the Company.",
  "Booking amount constitutes 10% of the Agreement value of the unit, which has to be paid while signing this form, failing which the Company reserves the right to revoke the allotment and charge 2% of the sale value as cancellation charges along with applicable GST.",
  "The Company reserves the right to cancel the allotment and forfeit the amount paid, if the applicant fails to pay the amount as per the terms of booking: (a) If the Applicant/s desires to cancel the booking before execution of sale agreement, 2% of the Sale Value along with applicable GST will be forfeited as cancellation fee and administrative charges and the balance amount paid will be refunded without interest within 60 days of acceptance of cancellation request by the Company. (b) If the Applicant/s desires to cancel the booking after execution of sale agreement, cancellation will be done at the sole discretion of the Company subject to forfeiture of 10% of the Sale Value with applicable GST and the balance amount received will be refunded without interest within 60 days of the cancellation agreement. In such an event, the Company is entitled to sell the apartment / commercial premise / unit to any other person and on such terms and conditions as the Company deems fit.",
  "No assignment will be considered.",
  "Dimensions and details provided in the brochure are approximate and are subject to alteration without notice.",
  "Expenses like Stamp Duty & Registration charges, additional stamp duty, if applicable, will have to be borne by the Applicant.",
  "If the Apartment / Unit being booked is valued Rs. 50 lakhs or more, kindly ensure to deduct 1% TDS, whenever the installment payment is released to the Company and also furnish Form 26QB, challan copy and duly signed Form 16(B) hard copy to the Company.",
  "Company reserves the right to cancel the allotment and forfeit money paid, if the amounts are not paid as per the terms of booking and / or if the amount paid by cheque is dishonoured and/or for non-availability of funds or for any of the reasons, and/or suppression of information / wrong information of any information which is required to be provided at the time of booking resulting in violation of the terms by the Applicant.",
];
const DECLARATION = "I / We confirm that all the details given above are true to my / our knowledge, and I / We have gone through the Terms and Conditions stated above and agree for the same. I / We further authorise Rohan Corporation India Pvt. Ltd. to contact me / us via phone, WhatsApp, email or SMS for booking-related communication.";
const ATTACH_DOCS = [
  "Self-attested Aadhaar card (both applicants)",
  "Self-attested PAN card (both applicants)",
  "Passport-size photograph (both applicants)",
  "Address proof (utility bill / passport copy)",
  "GPA copy, if signing through Power of Attorney",
  "Booking amount cheque / payment receipt",
];

const fLbl = { fontSize: 12, color: C.muted, display: "block", marginBottom: 5 };
const fInp = { width: "100%", outline: "none", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 11px", fontSize: 13, background: C.white, color: C.ink, boxSizing: "border-box" };
const fChip = (on) => ({ fontSize: 12, padding: "5px 11px", borderRadius: 99, cursor: "pointer", background: on ? C.accent : C.white, color: on ? "#fff" : C.muted, border: `1px solid ${on ? C.accent : C.line}` });

function Fld({ label, value, onChange, ph, span }) {
  return (<div style={{ gridColumn: span === 2 ? "span 2" : "span 1" }}><label style={fLbl}>{label}</label><input value={value || ""} onChange={onChange} placeholder={ph} style={fInp} /></div>);
}
function TxtA({ label, value, onChange }) {
  return (<div style={{ gridColumn: "span 2" }}><label style={fLbl}>{label}</label><textarea value={value || ""} onChange={onChange} rows={2} style={{ ...fInp, resize: "vertical" }} /></div>);
}
function Opt({ label, value, onChange, opts, span }) {
  return (<div style={{ gridColumn: span === 2 ? "span 2" : "span 1" }}><label style={fLbl}>{label}</label><div className="flex flex-wrap gap-1.5">{opts.map((o) => <button key={o} type="button" onClick={() => onChange(o)} style={fChip(value === o)}>{o}</button>)}</div></div>);
}
function Multi({ label, value = [], onChange, opts, span }) {
  const t = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (<div style={{ gridColumn: span === 1 ? "span 1" : "span 2" }}><label style={fLbl}>{label}</label><div className="flex flex-wrap gap-1.5">{opts.map((o) => <button key={o} type="button" onClick={() => t(o)} style={fChip(value.includes(o))}>{o}</button>)}</div></div>);
}
function Sec({ children }) {
  return (
    <div className="flex items-center gap-2.5" style={{ margin: "26px 0 14px" }}>
      <span style={{ width: 5, height: 17, borderRadius: 3, background: C.accent, flexShrink: 0 }} />
      <span style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: C.line }} />
    </div>
  );
}
function GridB({ children }) {
  return <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>{children}</div>;
}
function PhotoBox() {
  return (<div style={{ width: 116, flexShrink: 0 }}>
    <div className="flex items-center justify-center" style={{ height: 92, border: `1px dashed ${C.line}`, borderRadius: 8, background: C.card, fontSize: 11, color: C.faint, textAlign: "center", padding: 6 }}>Affix passport photo</div>
    <button type="button" style={{ marginTop: 6, width: "100%", fontSize: 11, padding: "5px", borderRadius: 7, border: `1px solid ${C.line}`, background: C.white, color: C.accent, cursor: "pointer" }}>Upload</button>
  </div>);
}
function AppBlock({ f, set, put, p, joint }) {
  return (
    <div className="flex gap-4" style={{ alignItems: "flex-start" }}>
      <PhotoBox />
      <div style={{ flex: 1, minWidth: 0 }}>
        <GridB>
          <Fld label="Full name" value={f[p + "name"]} onChange={set(p + "name")} ph="Full name" span={2} />
          <Fld label="Father's / Husband's name" value={f[p + "father"]} onChange={set(p + "father")} span={2} />
          {joint && <Opt label="Relation to applicant" value={f[p + "relation"]} onChange={(v) => put(p + "relation", v)} opts={["Spouse", "Parent", "Child", "Sibling", "Other"]} span={2} />}
          <Fld label="Date of birth" value={f[p + "dob"]} onChange={set(p + "dob")} ph="DD/MM/YYYY" />
          <Fld label="Age" value={f[p + "age"]} onChange={set(p + "age")} />
          <Opt label="Gender" value={f[p + "gender"]} onChange={(v) => put(p + "gender", v)} opts={GENDER} />
          <Opt label="Marital status" value={f[p + "marital"]} onChange={(v) => put(p + "marital", v)} opts={MARITAL} />
          <Fld label="Aadhaar number" value={f[p + "aadhaar"]} onChange={set(p + "aadhaar")} />
          <Fld label="PAN number" value={f[p + "pan"]} onChange={set(p + "pan")} />
          <Fld label="Phone" value={f[p + "phone"]} onChange={set(p + "phone")} />
          <Fld label="WhatsApp number" value={f[p + "whatsapp"]} onChange={set(p + "whatsapp")} />
          <Fld label="Email address" value={f[p + "email"]} onChange={set(p + "email")} />
          <Fld label="Nationality" value={f[p + "nationality"]} onChange={set(p + "nationality")} />
          <Fld label="Current address" value={f[p + "curaddr"]} onChange={set(p + "curaddr")} span={2} />
          <Fld label="Permanent address" value={f[p + "permaddr"]} onChange={set(p + "permaddr")} span={2} />
          <Fld label="State" value={f[p + "state"]} onChange={set(p + "state")} />
          <Fld label="Pin" value={f[p + "pin"]} onChange={set(p + "pin")} />
          <Fld label="Country of residence" value={f[p + "country"]} onChange={set(p + "country")} />
          <Opt label="Customer type" value={f[p + "custtype"]} onChange={(v) => put(p + "custtype", v)} opts={CUSTTYPE} />
          <Opt label="Employment type" value={f[p + "emptype"]} onChange={(v) => put(p + "emptype", v)} opts={EMPTYPE} span={2} />
          <Fld label="Company / business name" value={f[p + "company"]} onChange={set(p + "company")} />
          <Fld label="Job title / designation" value={f[p + "job"]} onChange={set(p + "job")} />
          <Fld label="Industry / sector" value={f[p + "industry"]} onChange={set(p + "industry")} span={2} />
        </GridB>
      </div>
    </div>
  );
}
function bookletHtml(f, files) {
  const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const val = (v) => (Array.isArray(v) ? v.join(", ") : v);
  const sec = (title, rows) => {
    const body = rows
      .filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0))
      .map(([l, v]) => `<tr><td class="l">${esc(l)}</td><td class="v">${esc(val(v))}</td></tr>`)
      .join("");
    return body ? `<h2>${esc(title)}</h2><table>${body}</table>` : "";
  };
  const sections = [
    sec("Primary applicant", [
      ["Full name", f.p_name], ["Father's / Husband's name", f.p_father], ["Date of birth", f.p_dob], ["Age", f.p_age],
      ["Gender", f.p_gender], ["Marital status", f.p_marital], ["Aadhaar", f.p_aadhaar], ["PAN", f.p_pan],
      ["Phone", f.p_phone], ["WhatsApp", f.p_whatsapp], ["Email", f.p_email], ["Nationality", f.p_nationality],
      ["Current address", f.p_curaddr], ["Permanent address", f.p_permaddr], ["State", f.p_state], ["Pin", f.p_pin],
      ["Country of residence", f.p_country], ["Customer type", f.p_custtype], ["Employment type", f.p_emptype],
      ["Company / business", f.p_company], ["Job title", f.p_job], ["Industry", f.p_industry],
    ]),
    sec("Nominee & GPA", [
      ["Nominee name", f.nomName], ["Relationship", f.nomRel], ["GPA holder", f.gpaName], ["GPA relationship", f.gpaRel], ["GPA address", f.gpaAddr],
    ]),
    sec("Property interest & booking", [
      ["Project name", f.project], ["Location", f.location], ["Project type", f.projectType], ["Configuration", f.config],
      ["Apt / Shop no.", f.unitNo], ["Tower / Block", f.tower], ["Floor", f.floor], ["Unit area / SBA", f.sba],
      ["Facing", f.facing], ["Purchase purpose", f.purpose],
    ]),
    sec("Booking amount submitted", [
      ["Booking amount (₹)", f.bookingAmount], ["Mode", f.payMode], ["Cheque / DD / Txn ref", f.txnRef], ["Dated", f.txnDate],
      ["From bank", f.fromBank], ["Branch", f.fromBranch], ["Account holder", f.acHolder],
    ]),
    sec("Home loan / financing", [
      ["Funding source", f.funding], ["Bank / institution", f.loanBank], ["Loan amount (₹)", f.loanAmount],
      ["Loan status", f.loanStatus], ["Sanction letter", f.sanctionLetter], ["Loan co-applicant", f.loanCoApp],
    ]),
    sec("Preferences & communication", [
      ["Vastu required", f.vastu], ["Floor preference", f.floorPref], ["Direction preference", f.dirPref],
      ["Specific unit preference", f.unitPref], ["Customizations", f.custom], ["Preferred communication", f.comm],
      ["Preferred language", f.lang], ["Best time to contact", f.bestTime],
    ]),
    sec("Office use — pricing", [
      ["Base rate (₹/sq ft)", f.baseRate], ["Offered rate (₹/sq ft)", f.offeredRate], ["Concluded rate (₹/sq ft)", f.concludedRate],
      ["Final sale value (₹)", f.finalSaleValue], ["Discount given (%)", f.discount], ["Discount approved by", f.discountBy],
      ["Payment plan", f.payPlan], ["Plan (other)", f.payPlanOther],
    ]),
    sec("Lead & enquiry tracking", [
      ["Lead source", f.leadSource], ["Lead date", f.leadDate], ["Project enquired", f.projectEnquired],
      ["Total site visits", f.siteVisits], ["First response (hrs)", f.firstResponse], ["Follow-ups", f.followups], ["Days lead-to-booking", f.leadToBooking],
    ]),
    sec("Sales & channel attribution", [
      ["Sales executive", f.salesExec], ["Team / branch", f.team], ["Channel partner", f.channelPartner], ["Agency", f.agency],
      ["Through agent", f.throughAgent], ["Agent name", f.agentName], ["Agent contact", f.agentContact], ["Commission %", f.commission],
    ]),
    sec("Referral", [
      ["Referred customer", f.refName], ["Phone", f.refPhone], ["Referral date", f.refDate], ["Project referred", f.refProject],
      ["Reward status", f.refReward], ["Amount (₹)", f.refAmount],
    ]),
    sec("Verification & sign-off", [
      ["Verified by", f.verifiedBy], ["Date of verification", f.verifyDate], ["Received by", f.receivedBy],
      ["Approved by (manager)", f.approvedBy], ["CRM entry done", f.crmDone], ["CRM lead ID", f.crmLeadId], ["Internal remarks", f.remarks],
    ]),
  ].join("");
  const docs = (files && files.length)
    ? `<h2>Uploaded documents</h2><ul>${files.map((x) => `<li>${esc(x.name)}</li>`).join("")}</ul>`
    : "";
  return `<!doctype html><html><head><meta charset="utf-8"><title>Booking booklet</title>
<style>
  *{box-sizing:border-box}
  body{font-family:Arial,Helvetica,sans-serif;color:#3a2c1b;margin:0;padding:28px 34px;background:#fff;font-size:12.5px}
  .hd{border-bottom:2px solid #6E4B1E;padding-bottom:10px;margin-bottom:16px}
  .hd .t{font-size:20px;font-weight:700;color:#4A3214}
  .hd .s{font-size:12px;color:#7C6F5E;margin-top:2px}
  h2{font-size:13px;color:#6E4B1E;border-bottom:1px solid #EAE5DB;padding-bottom:4px;margin:18px 0 6px}
  table{width:100%;border-collapse:collapse}
  td{padding:3px 6px;vertical-align:top;border-bottom:1px solid #F2EFE8}
  td.l{width:38%;color:#7C6F5E}
  td.v{color:#3a2c1b;font-weight:500}
  ul{margin:6px 0;padding-left:20px}
  li{padding:2px 0}
</style></head><body>
<div class="hd"><div class="t">Rohan Corporation — Booking Form</div><div class="s">${esc(f.p_name || "Untitled")}${f.project ? " · " + esc(f.project) : ""}${f.unitNo ? " · " + esc(f.unitNo) : ""}</div></div>
${sections}${docs}
</body></html>`;
}

function PageTab({ n, label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-2" style={{
      background: active ? C.accent : C.white, color: active ? "#fff" : C.muted,
      border: `1px solid ${active ? C.accent : C.line}`, borderRadius: 10, padding: "8px 14px",
      fontSize: 13, fontWeight: 600, cursor: "pointer",
    }}>
      <span className="flex items-center justify-center rounded-full" style={{ width: 20, height: 20, fontSize: 11, background: active ? "rgba(255,255,255,0.25)" : C.accentSoft, color: active ? "#fff" : C.accent }}>{n}</span>
      {label}
    </button>
  );
}

function NewBooking({ initial, editing, onSave, plans = [] }) {
  const [f, setF] = useState(initial && Object.keys(initial).length ? initial : { p_nationality: "Indian", p_country: "India" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const put = (k, v) => setF((x) => ({ ...x, [k]: v }));
  // Pricing: concluded rate and discount % are reciprocal (ref = offered, else base rate).
  // Final sale value auto-fills from concluded rate × SBA.
  const onConcluded = (e) => {
    const v = e.target.value;
    setF((x) => {
      const ref = Number(x.offeredRate) || Number(x.baseRate) || 0;
      const sba = Number(x.sba) || 0, cr = Number(v) || 0;
      const next = { ...x, concludedRate: v };
      if (ref > 0 && v !== "") next.discount = String(Math.round((1 - cr / ref) * 1000) / 10);
      if (sba > 0 && v !== "") next.finalSaleValue = String(Math.round(cr * sba));
      return next;
    });
  };
  const onDiscount = (e) => {
    const v = e.target.value;
    setF((x) => {
      const ref = Number(x.offeredRate) || Number(x.baseRate) || 0;
      const sba = Number(x.sba) || 0, d = Number(v) || 0;
      const next = { ...x, discount: v };
      if (ref > 0 && v !== "") { const cr = Math.round(ref * (1 - d / 100)); next.concludedRate = String(cr); if (sba > 0) next.finalSaleValue = String(Math.round(cr * sba)); }
      return next;
    });
  };
  const ready = f.p_name && f.project && (f.finalSaleValue || f.bookingAmount);
  const canDraft = !!(f.p_name || f.project);
  const [page, setPage] = useState(1);
  const [shared, setShared] = useState(false);
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [scanPct, setScanPct] = useState(0);
  const [scanErr, setScanErr] = useState("");
  const [scanDoc, setScanDoc] = useState("");
  const [conflicts, setConflicts] = useState(null);
  // OCR one file (image or PDF) and return parsed applicant fields.
  const ocrOne = async (file) => {
    let input = file;
    if (file.type === "application/pdf") input = await pdfFirstPageToCanvas(file);
    const { data } = await Tesseract.recognize(input, "eng", {
      logger: (m) => { if (m.status === "recognizing text") setScanPct(Math.round(m.progress * 100)); },
    });
    return parseIdText(data.text);
  };
  const autofillFromDoc = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setScanning(true); setAutofilled(false); setScanErr(""); setScanPct(0); setConflicts(null);
    try {
      // OCR each document in turn.
      const parsedList = [];
      for (let i = 0; i < picked.length; i++) {
        setScanDoc(picked.length > 1 ? `${i + 1}/${picked.length}` : "");
        setScanPct(0);
        parsedList.push({ file: picked[i].name, fields: await ocrOne(picked[i]) });
      }
      // Collect every value seen per field, across all documents.
      const byKey = {};
      parsedList.forEach((p) => Object.entries(p.fields).forEach(([k, v]) => { (byKey[k] = byKey[k] || []).push({ v, file: p.file }); }));
      const norm = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
      const FIELD_LABELS = { p_name: "Name", p_curaddr: "Address", p_aadhaar: "Aadhaar no.", p_pan: "PAN", p_dob: "Date of birth", p_pin: "PIN code", p_state: "State", p_gender: "Gender" };
      const merged = {};
      const conf = [];
      for (const [k, arr] of Object.entries(byKey)) {
        const distinct = [...new Set(arr.map((a) => norm(a.v)))].filter(Boolean);
        if (distinct.length > 1) {
          // Same field, different value across documents — flag, do not auto-fill.
          conf.push({ field: k, label: FIELD_LABELS[k] || k, values: arr.map((a) => `${a.v} (${a.file})`) });
        } else {
          merged[k] = arr[0].v;
        }
      }
      const keys = Object.keys(merged);
      if (keys.length === 0 && conf.length === 0) {
        setScanErr("Couldn't read any details. Try clearer, well-lit, straight images.");
      } else {
        if (keys.length) {
          setF((x) => { const m = { ...x }; for (const k of keys) if (!m[k]) m[k] = merged[k]; return m; });
          setAutofilled(true); setTimeout(() => setAutofilled(false), 5000);
        }
        if (conf.length) setConflicts(conf);
      }
    } catch (err) {
      setScanErr("OCR failed: " + (err && err.message ? err.message : "unknown error"));
    } finally { setScanning(false); setScanDoc(""); }
  };
  const shareWithCustomer = () => {
    const link = "https://post-sales.rohancorporation.com/intake/" + (f.p_phone || "new");
    try { navigator.clipboard && navigator.clipboard.writeText(link); } catch { /* ignore */ }
    setShared(true); setTimeout(() => setShared(false), 2500);
  };
  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []).map((x) => ({ name: x.name, size: x.size }));
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };
  const onSignedFiles = (e) => {
    const picked = Array.from(e.target.files || []).map((x) => ({ name: x.name, size: x.size, signed: true }));
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = "";
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const downloadBooklet = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(bookletHtml(f, files));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };
  return (
    <div style={{ maxWidth: 940, margin: "0 auto" }}>
      {conflicts && (
        <div onClick={() => setConflicts(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.white, borderRadius: 14, padding: "22px 24px", maxWidth: 460, width: "100%", boxShadow: "0 20px 50px rgba(0,0,0,.25)" }}>
            <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
              <AlertTriangle size={18} color={C.amber} />
              <span style={{ fontFamily: display, fontSize: 17, fontWeight: 600 }}>Documents don't match</span>
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
              The {conflicts.map((c) => c.label).join(", ")} {conflicts.length > 1 ? "are" : "is"} different across the uploaded documents. These fields were left blank — please fill them in yourself.
            </div>
            {conflicts.map((c) => (
              <div key={c.field} style={{ marginBottom: 10, padding: "8px 10px", background: C.card, borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.ink, marginBottom: 3 }}>{c.label}</div>
                {c.values.map((v, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.faint }}>• {v}</div>
                ))}
              </div>
            ))}
            <div className="flex justify-end" style={{ marginTop: 14 }}>
              <button onClick={() => setConflicts(null)} style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Got it, I'll fill these</button>
            </div>
          </div>
        </div>
      )}
      <Card>
        <div className="flex items-start justify-between flex-wrap gap-3" style={{ marginBottom: 4, paddingBottom: 14, borderBottom: `1px solid ${C.line}` }}>
          <div>
            <div className="flex items-center gap-2"><UserPlus size={18} color={C.accent} /><span style={{ fontFamily: display, fontSize: 20, fontWeight: 600 }}>{editing ? "Continue booking (draft)" : "Booking form"}</span></div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>Mirrors your printed booking form. {editing ? "Editing a saved draft." : "Save as draft anytime; commit needs name, project & sale value."}</div>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={!canDraft} onClick={() => onSave(f, true)} className="flex items-center gap-1.5" style={{ background: C.white, color: canDraft ? C.accent : C.faint, border: `1px solid ${canDraft ? C.accent : C.line}`, borderRadius: 10, padding: "9px 14px", fontSize: 13, fontWeight: 600, cursor: canDraft ? "pointer" : "not-allowed" }}><FileText size={15} /> Save as draft</button>
            <button disabled={!ready} onClick={() => onSave(f, false)} className="flex items-center gap-1.5" style={{ background: ready ? C.accent : C.line, color: ready ? "#fff" : C.faint, border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: ready ? "pointer" : "not-allowed" }}><Save size={15} /> {editing ? "Commit" : "Create booking"}</button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap" style={{ margin: "16px 0 18px" }}>
          <PageTab n={1} label="Customer details" active={page === 1} onClick={() => setPage(1)} />
          <PageTab n={2} label="Terms & conditions" active={page === 2} onClick={() => setPage(2)} />
          <PageTab n={3} label="Office use" active={page === 3} onClick={() => setPage(3)} />
          <PageTab n={4} label="Documents" active={page === 4} onClick={() => setPage(4)} />
          <PageTab n={5} label="Preview" active={page === 5} onClick={() => setPage(5)} />
          <span style={{ fontSize: 12, color: C.faint, marginLeft: 4 }}>
            {page === 1 ? "Filled by the customer" : page === 2 ? "Booking terms & customer consent" : page === 3 ? "Internal — sales / manager only" : page === 4 ? "Attach supporting files" : "Generate & download the booklet"}
          </span>
        </div>

        <div style={{ display: page === 1 ? "block" : "none" }}>
        <Sec>Primary applicant — information</Sec>
        <div className="flex items-center gap-3 flex-wrap" style={{ marginBottom: 14, padding: "12px 14px", background: C.accentSoft, borderRadius: 12 }}>
          <label className="flex items-center gap-2" style={{ cursor: scanning ? "default" : "pointer", background: C.accent, color: "#fff", borderRadius: 9, padding: "9px 14px", fontSize: 13, fontWeight: 600, opacity: scanning ? 0.7 : 1 }}>
            <input type="file" accept="image/*,application/pdf" multiple disabled={scanning} style={{ display: "none" }} onChange={autofillFromDoc} />
            <Upload size={15} /> {scanning ? `Reading${scanDoc ? ` ${scanDoc}` : ""}… ${scanPct}%` : "Upload IDs to auto-fill"}
          </label>
          <span style={{ fontSize: 12.5, color: scanErr ? C.red : scanning ? C.amber : autofilled ? C.green : C.muted }}>
            {scanErr ? scanErr : scanning ? `Reading document ${scanDoc}… ${scanPct}%` : autofilled ? "✓ Fields auto-filled from the scanned IDs" : "Upload one or more IDs (Aadhaar / PAN — photo or PDF) to auto-fill & cross-check the applicant fields"}
          </span>
        </div>
        <AppBlock f={f} set={set} put={put} p="p_" joint={false} />

        {(f.jointIds || []).map((id, i) => (
          <div key={id}>
            <div className="flex items-center gap-2.5" style={{ margin: "26px 0 14px" }}>
              <span style={{ width: 5, height: 17, borderRadius: 3, background: C.accent, flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>Joint applicant {i + 1} — information</span>
              <span style={{ flex: 1, height: 1, background: C.line }} />
              <button type="button" onClick={() => put("jointIds", f.jointIds.filter((x) => x !== id))} className="flex items-center gap-1" style={{ fontSize: 12, color: C.red, background: C.redSoft, border: "none", borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}><Trash2 size={13} /> Remove</button>
            </div>
            <AppBlock f={f} set={set} put={put} p={`j${id}_`} joint={true} />
          </div>
        ))}
        <button type="button" onClick={() => put("jointIds", [...(f.jointIds || []), (f.jointIds && f.jointIds.length ? Math.max(...f.jointIds) + 1 : 0)])} className="flex items-center gap-1.5" style={{ marginTop: 16, fontSize: 13, color: C.accent, background: C.accentSoft, border: "none", borderRadius: 8, padding: "9px 14px", cursor: "pointer", fontWeight: 600 }}><Plus size={15} /> Add joint applicant</button>

        <Sec>Nominee &amp; GPA details</Sec>
        <GridB>
          <Fld label="Nominee name" value={f.nomName} onChange={set("nomName")} />
          <Fld label="Relationship to applicant" value={f.nomRel} onChange={set("nomRel")} />
          <Fld label="GPA holder name (if signing through GPA)" value={f.gpaName} onChange={set("gpaName")} />
          <Fld label="GPA relationship" value={f.gpaRel} onChange={set("gpaRel")} />
          <Fld label="GPA address" value={f.gpaAddr} onChange={set("gpaAddr")} span={2} />
        </GridB>

        <Sec>Home loan / financing details</Sec>
        <GridB>
          <Opt label="Funding source" value={f.funding} onChange={(v) => put("funding", v)} opts={FUNDING} span={2} />
          <Fld label="Bank / financial institution" value={f.loanBank} onChange={set("loanBank")} />
          <Fld label="Loan amount required (₹)" value={f.loanAmount} onChange={set("loanAmount")} />
          <Opt label="Loan status" value={f.loanStatus} onChange={(v) => put("loanStatus", v)} opts={LOANSTATUS} span={2} />
          <Opt label="Sanction letter available?" value={f.sanctionLetter} onChange={(v) => put("sanctionLetter", v)} opts={YESNO} />
          <Fld label="Co-applicant for loan" value={f.loanCoApp} onChange={set("loanCoApp")} />
        </GridB>

        <Sec>Preferences &amp; communication</Sec>
        <GridB>
          <Opt label="Vastu compliance required?" value={f.vastu} onChange={(v) => put("vastu", v)} opts={YESNO} />
          <Opt label="Floor preference" value={f.floorPref} onChange={(v) => put("floorPref", v)} opts={FLOORPREF} />
          <Fld label="Direction preference" value={f.dirPref} onChange={set("dirPref")} />
          <Fld label="Specific unit preference" value={f.unitPref} onChange={set("unitPref")} />
          <TxtA label="Customizations / modifications requested" value={f.custom} onChange={set("custom")} />
          <Multi label="Preferred communication" value={f.comm} onChange={(v) => put("comm", v)} opts={COMM} />
          <Opt label="Preferred language" value={f.lang} onChange={(v) => put("lang", v)} opts={LANG} span={2} />
          <Opt label="Best time to contact" value={f.bestTime} onChange={(v) => put("bestTime", v)} opts={BESTTIME} span={2} />
        </GridB>

        <Sec>Documents received / to attach</Sec>
        <GridB>
          <Multi label="KYC documents" value={f.kyc} onChange={(v) => put("kyc", v)} opts={DOCS} />
        </GridB>

        <Sec>Customer insights</Sec>
        <GridB>
          <Multi label={`How did you hear about ${BRAND}?`} value={f.heard} onChange={(v) => put("heard", v)} opts={HEARD} />
          <Multi label={`What made you finalise ${BRAND}?`} value={f.finalised} onChange={(v) => put("finalised", v)} opts={FINALISED} />
          <TxtA label="Anything else in their words?" value={f.insightNotes} onChange={set("insightNotes")} />
        </GridB>

        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 24 }}>
          <button type="button" onClick={() => setPage(2)} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Next: Terms &amp; conditions →</button>
          <button type="button" disabled={!canDraft} onClick={() => onSave(f, true)} className="flex items-center gap-1.5" style={{ background: C.white, color: canDraft ? C.accent : C.faint, border: `1px solid ${canDraft ? C.accent : C.line}`, borderRadius: 10, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: canDraft ? "pointer" : "not-allowed" }}><Save size={16} /> Save as draft</button>
          <button type="button" onClick={shareWithCustomer} className="flex items-center gap-1.5" style={{ background: C.white, color: C.accent, border: `1px solid ${C.accent}`, borderRadius: 10, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}><Smartphone size={16} /> Share with customer</button>
          <span style={{ fontSize: 12, color: shared ? C.green : C.faint }}>{shared ? "✓ Customer intake link copied (demo — backend pending)" : "Send page 1 to the customer's phone to self-fill"}</span>
        </div>
        </div>

        <div style={{ display: page === 2 ? "block" : "none" }}>
        <Sec>Documents to be attached with this form</Sec>
        <div className="rounded-xl p-4" style={{ border: `1px solid ${C.line}`, background: C.card }}>
          <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 7, listStyle: "none" }}>
            {ATTACH_DOCS.map((d, i) => <li key={i} className="flex items-start gap-2" style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}><span style={{ color: C.accent, fontWeight: 700, lineHeight: 1.4 }}>•</span>{d}</li>)}
          </ul>
        </div>

        <Sec>General terms &amp; conditions</Sec>
        <div className="rounded-xl p-4" style={{ border: `1px solid ${C.line}`, background: C.card }}>
          <ul style={{ margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10, listStyle: "none" }}>
            {TERMS.map((t, i) => <li key={i} className="flex items-start gap-2" style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}><span style={{ color: C.accent, fontWeight: 700, lineHeight: 1.4, flexShrink: 0 }}>•</span>{t}</li>)}
          </ul>
        </div>

        <Sec>Declaration</Sec>
        <div className="rounded-xl p-4" style={{ border: `1px solid ${C.accent}`, background: C.accentSoft }}>
          <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.6, fontStyle: "italic" }}>{DECLARATION}</div>
          <button type="button" onClick={() => put("termsAgreed", !f.termsAgreed)} className="flex items-center gap-2.5 mt-4" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
            {f.termsAgreed
              ? <CheckCircle2 size={20} color={C.green} style={{ flexShrink: 0 }} />
              : <span style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${C.accent}`, flexShrink: 0, display: "inline-block" }} />}
            <span style={{ fontSize: 13, fontWeight: 600, color: f.termsAgreed ? C.green : C.ink }}>The applicant(s) confirm the above declaration and agree to the terms &amp; conditions.</span>
          </button>
        </div>
        <div style={{ height: 12 }} />
        <GridB>
          <Fld label="Signatory name (Applicant 1)" value={f.termsAcceptedBy} onChange={set("termsAcceptedBy")} />
          <Fld label="Date of acceptance" value={f.termsDate} onChange={set("termsDate")} />
          <Fld label="Place" value={f.termsPlace} onChange={set("termsPlace")} />
        </GridB>

        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 24 }}>
          <button type="button" onClick={() => setPage(1)} className="flex items-center gap-1.5" style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 10, padding: "11px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}><ArrowLeft size={16} /> Customer details</button>
          <button type="button" onClick={() => setPage(3)} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Next: Office use →</button>
          <button type="button" disabled={!canDraft} onClick={() => onSave(f, true)} className="flex items-center gap-1.5" style={{ background: C.white, color: canDraft ? C.accent : C.faint, border: `1px solid ${canDraft ? C.accent : C.line}`, borderRadius: 10, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: canDraft ? "pointer" : "not-allowed" }}><Save size={16} /> Save as draft</button>
        </div>
        </div>

        <div style={{ display: page === 3 ? "block" : "none" }}>
        <Sec>Property interest &amp; booking details</Sec>
        <GridB>
          <div style={{ gridColumn: "span 1" }}>
            <label style={fLbl}>Project name (selects the milestone plan)</label>
            <BrandSelect value={f.project || ""} placeholder="Select project…"
              options={Array.from(new Set([...plans.map((p) => p.name), ...(f.project ? [f.project] : [])]))}
              onChange={(v) => { const pl = plans.find((p) => p.name === v); put("project", v); if (pl && !f.location) put("location", pl.location); }} />
          </div>
          <Fld label="Location" value={f.location} onChange={set("location")} />
          <Opt label="Project type" value={f.projectType} onChange={(v) => put("projectType", v)} opts={PROJTYPE} />
          <Opt label="Configuration" value={f.config} onChange={(v) => put("config", v)} opts={CONFIG} />
          <Fld label="Apt / Shop no." value={f.unitNo} onChange={set("unitNo")} />
          <Fld label="Tower / Block" value={f.tower} onChange={set("tower")} />
          <Fld label="Floor no." value={f.floor} onChange={set("floor")} />
          <Fld label="Unit area / SBA (sq ft)" value={f.sba} onChange={set("sba")} />
          <Opt label="Facing" value={f.facing} onChange={(v) => put("facing", v)} opts={FACING} />
          <Opt label="Purchase purpose" value={f.purpose} onChange={(v) => put("purpose", v)} opts={PURPOSE} />
        </GridB>
        {(() => {
          const pl = plans.find((p) => p.name === f.project);
          if (!pl) return f.project ? <div style={{ marginTop: 12, fontSize: 12.5, color: C.amber, background: C.amberSoft, borderRadius: 10, padding: "10px 14px" }}>No milestone plan for "{f.project}". A generic schedule will be used — add a plan under Milestones to auto-generate the full schedule.</div> : null;
          const amt = Number(f.finalSaleValue) || 0;
          const sum = pl.milestones.reduce((x, mm) => x + mm.pct, 0);
          return (
            <div style={{ marginTop: 12, fontSize: 12.5, color: C.accent, background: C.accentSoft, borderRadius: 10, padding: "10px 14px" }}>
              ✓ Plan <b>{pl.name}</b> · {pl.milestones.length} milestones · totals {sum}%
              {amt ? <> — schedule auto-built for {inr(amt)} (each instalment = % × sale value).</> : <> — enter the final sale value below; amounts scale to it.</>}
            </div>
          );
        })()}

        <Sec>Booking amount submitted</Sec>
        <GridB>
          <Fld label="Booking amount (₹)" value={f.bookingAmount} onChange={set("bookingAmount")} />
          <Opt label="Mode" value={f.payMode} onChange={(v) => put("payMode", v)} opts={MODE} />
          <Fld label="Cheque / DD / Txn reference no." value={f.txnRef} onChange={set("txnRef")} />
          <Fld label="Dated" value={f.txnDate} onChange={set("txnDate")} />
          <Fld label="From bank" value={f.fromBank} onChange={set("fromBank")} />
          <Fld label="Branch" value={f.fromBranch} onChange={set("fromBranch")} />
          <Fld label="Account holder name" value={f.acHolder} onChange={set("acHolder")} span={2} />
        </GridB>

        <Sec>Office use — pricing analysis</Sec>
        <GridB>
          <Fld label="Base rate (₹/sq ft)" value={f.baseRate} onChange={set("baseRate")} />
          <Fld label="Offered rate (₹/sq ft)" value={f.offeredRate} onChange={set("offeredRate")} />
          <Fld label="Concluded rate (₹/sq ft)" value={f.concludedRate} onChange={onConcluded} />
          <Fld label="Final sale value (₹)" value={f.finalSaleValue} onChange={set("finalSaleValue")} />
          <Fld label="Discount given (%)" value={f.discount} onChange={onDiscount} />
          <Fld label="Discount approved by" value={f.discountBy} onChange={set("discountBy")} />
          <Opt label="Payment plan" value={f.payPlan} onChange={(v) => put("payPlan", v)} opts={PAYPLAN} span={2} />
          <Fld label="If other, specify plan" value={f.payPlanOther} onChange={set("payPlanOther")} span={2} />
        </GridB>

        <Sec>Lead &amp; enquiry tracking</Sec>
        <GridB>
          <Opt label="Lead source" value={f.leadSource} onChange={(v) => put("leadSource", v)} opts={LEADSRC} span={2} />
          <Fld label="Lead date" value={f.leadDate} onChange={set("leadDate")} />
          <Fld label="Project enquired" value={f.projectEnquired} onChange={set("projectEnquired")} />
          <Fld label="Total site visits" value={f.siteVisits} onChange={set("siteVisits")} />
          <Fld label="First response time (hrs)" value={f.firstResponse} onChange={set("firstResponse")} />
          <Fld label="No. of follow-ups" value={f.followups} onChange={set("followups")} />
          <Fld label="Days lead-to-booking" value={f.leadToBooking} onChange={set("leadToBooking")} />
        </GridB>

        <Sec>Sales &amp; channel attribution</Sec>
        <GridB>
          <Fld label="Sales executive" value={f.salesExec} onChange={set("salesExec")} />
          <Fld label="Team / branch" value={f.team} onChange={set("team")} />
          <Fld label="Channel partner" value={f.channelPartner} onChange={set("channelPartner")} />
          <Fld label="Agency name" value={f.agency} onChange={set("agency")} />
          <Opt label="Booking through agent?" value={f.throughAgent} onChange={(v) => put("throughAgent", v)} opts={YESNO} />
          <Fld label="Agent's name" value={f.agentName} onChange={set("agentName")} />
          <Fld label="Agent contact no." value={f.agentContact} onChange={set("agentContact")} />
          <Fld label="Commission %" value={f.commission} onChange={set("commission")} />
        </GridB>

        <Sec>Referral details</Sec>
        <GridB>
          <Fld label="Referred customer name" value={f.refName} onChange={set("refName")} />
          <Fld label="Referred customer phone" value={f.refPhone} onChange={set("refPhone")} />
          <Fld label="Referral date" value={f.refDate} onChange={set("refDate")} />
          <Fld label="Project referred for" value={f.refProject} onChange={set("refProject")} />
          <Opt label="Referral reward status" value={f.refReward} onChange={(v) => put("refReward", v)} opts={REWARD} />
          <Fld label="Amount (₹)" value={f.refAmount} onChange={set("refAmount")} />
        </GridB>

        <Sec>Verification &amp; sign-off</Sec>
        <GridB>
          <Fld label="Documents verified by" value={f.verifiedBy} onChange={set("verifiedBy")} />
          <Fld label="Date of verification" value={f.verifyDate} onChange={set("verifyDate")} />
          <Fld label="Received by" value={f.receivedBy} onChange={set("receivedBy")} />
          <Fld label="Approved by (manager)" value={f.approvedBy} onChange={set("approvedBy")} />
          <Opt label="CRM entry done?" value={f.crmDone} onChange={(v) => put("crmDone", v)} opts={YESNO} />
          <Fld label="CRM lead ID" value={f.crmLeadId} onChange={set("crmLeadId")} />
          <TxtA label="Internal remarks / observations" value={f.remarks} onChange={set("remarks")} />
        </GridB>

        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 24 }}>
          <button type="button" onClick={() => setPage(2)} className="flex items-center gap-1.5" style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 10, padding: "11px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}><ArrowLeft size={16} /> Terms &amp; conditions</button>
          <button type="button" onClick={() => setPage(4)} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Next: Documents →</button>
          <button type="button" disabled={!canDraft} onClick={() => onSave(f, true)} className="flex items-center gap-1.5" style={{ background: C.white, color: canDraft ? C.accent : C.faint, border: `1px solid ${canDraft ? C.accent : C.line}`, borderRadius: 10, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: canDraft ? "pointer" : "not-allowed" }}><Save size={16} /> Save as draft</button>
        </div>
        </div>

        <div style={{ display: page === 4 ? "block" : "none" }}>
        <Sec>Upload documents</Sec>
        <label className="flex flex-col items-center justify-center" style={{ border: `1px dashed ${C.accent}`, borderRadius: 12, background: C.accentSoft, padding: "26px", cursor: "pointer", textAlign: "center" }}>
          <input type="file" multiple style={{ display: "none" }} onChange={onFiles} />
          <Upload size={22} color={C.accent} />
          <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 600, color: C.accent }}>Click to upload documents</div>
          <div style={{ marginTop: 2, fontSize: 12, color: C.muted }}>Aadhaar, PAN, agreement, cheque copy, photos — any file type</div>
        </label>
        {files.length > 0 && (
          <div style={{ marginTop: 14 }}>
            {files.map((file, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2.5" style={{ borderBottom: i < files.length - 1 ? `1px solid ${C.line}` : "none" }}>
                <FileText size={15} color={C.accent} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</span>
                <span style={{ fontSize: 11, color: C.faint, flexShrink: 0 }}>{(file.size / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => removeFile(i)} style={{ background: "none", border: "none", cursor: "pointer", color: C.red, display: "flex", flexShrink: 0, padding: 2 }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 24 }}>
          <button type="button" onClick={() => setPage(3)} className="flex items-center gap-1.5" style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 10, padding: "11px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}><ArrowLeft size={16} /> Office use</button>
          <button type="button" onClick={() => setPage(5)} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Next: Preview →</button>
          <button type="button" disabled={!canDraft} onClick={() => onSave(f, true)} className="flex items-center gap-1.5" style={{ background: C.white, color: canDraft ? C.accent : C.faint, border: `1px solid ${canDraft ? C.accent : C.line}`, borderRadius: 10, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: canDraft ? "pointer" : "not-allowed" }}><Save size={16} /> Save as draft</button>
        </div>
        </div>

        <div style={{ display: page === 5 ? "block" : "none" }}>
        <div className="flex items-center justify-between flex-wrap gap-2" style={{ margin: "0 0 14px" }}>
          <div className="flex items-center gap-2.5">
            <span style={{ width: 5, height: 17, borderRadius: 3, background: C.accent, flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>Booklet preview</span>
          </div>
          <button type="button" onClick={downloadBooklet} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Download size={15} /> Download / Print booklet</button>
        </div>
        <iframe title="Booking booklet preview" srcDoc={bookletHtml(f, files)} style={{ width: "100%", height: 460, border: `1px solid ${C.line}`, borderRadius: 12, background: "#fff" }} />

        <Sec>Signed booking form</Sec>
        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 10 }}>Download / print the booklet above, get it signed by the customer, scan it, then upload the signed copy here. It is added to the booking documentation.</div>
        <label className="flex flex-col items-center justify-center" style={{ border: `1px dashed ${C.accent}`, borderRadius: 12, background: C.accentSoft, padding: "22px", cursor: "pointer", textAlign: "center" }}>
          <input type="file" accept="image/*,application/pdf" multiple style={{ display: "none" }} onChange={onSignedFiles} />
          <Upload size={22} color={C.accent} />
          <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 600, color: C.accent }}>Upload signed booking form</div>
          <div style={{ marginTop: 2, fontSize: 12, color: C.muted }}>Scanned PDF or photos of the signed booklet</div>
        </label>
        {files.filter((x) => x.signed).length > 0 && (
          <div style={{ marginTop: 14 }}>
            {files.map((file, i) => file.signed ? (
              <div key={i} className="flex items-center gap-2.5 py-2.5" style={{ borderBottom: `1px solid ${C.line}` }}>
                <CheckCircle2 size={15} color={C.green} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file.name}</span>
                <span style={{ fontSize: 11, color: C.green, flexShrink: 0 }}>signed</span>
                <button type="button" onClick={() => removeFile(i)} style={{ background: "none", border: "none", cursor: "pointer", color: C.red, display: "flex", flexShrink: 0, padding: 2 }}><Trash2 size={14} /></button>
              </div>
            ) : null)}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 24 }}>
          <button type="button" onClick={() => setPage(4)} className="flex items-center gap-1.5" style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 10, padding: "11px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}><ArrowLeft size={16} /> Documents</button>
          <button disabled={!ready} onClick={() => onSave(f, false)} className="flex items-center gap-1.5" style={{ background: ready ? C.accent : C.line, color: ready ? "#fff" : C.faint, border: "none", borderRadius: 10, padding: "11px 20px", fontSize: 14, fontWeight: 600, cursor: ready ? "pointer" : "not-allowed" }}><Save size={16} /> {editing ? "Commit booking" : "Create booking"}</button>
          <button disabled={!canDraft} onClick={() => onSave(f, true)} className="flex items-center gap-1.5" style={{ background: C.white, color: canDraft ? C.accent : C.faint, border: `1px solid ${canDraft ? C.accent : C.line}`, borderRadius: 10, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: canDraft ? "pointer" : "not-allowed" }}><FileText size={16} /> Save as draft</button>
          <span style={{ fontSize: 12, color: C.faint }}>{ready ? "Ready to commit" : canDraft ? "Can be saved as draft now" : "Enter a name or project to begin"}</span>
        </div>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- collections ---------------- */
// Auto-generate a printable construction-linked demand / payment-request letter for one milestone.
function demandLetterHtml(a, s, bank) {
  const esc = (v) => String(v == null ? "" : v).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const today = new Date().toLocaleDateString("en-GB");
  const city = (a.form && (a.form.p_city || a.form.city)) || "Mangalore";
  const totalCost = a.salePrice || 0;
  // Milestones "raised" so far = already paid + the one being demanded (status overdue/due).
  const raised = a.schedule.filter((x) => x.status === "paid" || x === s || x.status === "overdue");
  const pctOf = (amt) => totalCost ? (Math.round((amt / totalCost) * 10000) / 100) : 0;
  const rows = raised.map((x) => `<tr><td>${esc(x.label)}</td><td class="c">${pctOf(x.amt)}%</td><td class="r">${esc(inr(x.amt))}</td></tr>`).join("");
  const subtotal = raised.reduce((t, x) => t + x.amt, 0);
  const received = money(a).paid;
  const balance = subtotal - received;
  const pen = penaltyOf(s);
  const B = bank || COMPANY.bank;
  // Locked letterhead (header + footer must not change) — body injected in .content.
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Demand Letter — ${esc(a.name)}</title>
<style>
  @page { size: A4; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body { background: #e9e9e9; font-family: "Segoe UI","Calibri","Helvetica Neue",Arial,sans-serif; }
  .page { position: relative; width: 210mm; height: 297mm; margin: 10mm auto; background: #ffffff; box-shadow: 0 2px 14px rgba(0,0,0,0.18); overflow: hidden; box-sizing: border-box; }
  .logo { position: absolute; left: 11.1mm; top: 9.1mm; width: 24.3mm; height: 35.9mm; }
  .logo img { display: block; width: 100%; height: 100%; object-fit: contain; }
  .cin { position: absolute; top: 8.6mm; right: 8.9mm; font-size: 11pt; color: #1c1b1b; letter-spacing: .2px; }
  .header-text { position: absolute; top: 22.5mm; left: 35.4mm; right: 8.9mm; text-align: center; }
  .company-name { font-size: 28pt; font-weight: 700; color: #5c3102; line-height: 1; margin: 0; white-space: nowrap; }
  .company-sub  { font-size: 15pt; font-weight: 400; color: #5c3102; margin: 2.0mm 0 0 0; line-height: 1; white-space: nowrap; }
  .footer-rule { position: absolute; left: 5.8mm; right: 5.8mm; top: 272.3mm; border: 0; border-top: 1px solid #6b6b6b; margin: 0; }
  .footer-text { position: absolute; left: 0; right: 0; top: 277.0mm; text-align: center; font-size: 15pt; color: #5c3102; line-height: 1.45; }
  .content { position: absolute; left: 14mm; right: 14mm; top: 62mm; bottom: 30mm; font-size: 11pt; color: #1c1b1b; line-height: 1.5; overflow: hidden; }
  .content p { margin: 7px 0; }
  .content .row { display: flex; justify-content: space-between; align-items: flex-start; }
  .content .sub { text-align: center; font-weight: bold; text-decoration: underline; margin: 12px 0 8px; }
  table.pay { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 10.5pt; }
  table.pay td { border: 1px solid #999; padding: 5px 8px; vertical-align: middle; }
  table.pay td.c { text-align: center; white-space: nowrap; } table.pay td.r { text-align: right; white-space: nowrap; }
  table.pay tr.head td, table.pay tr.tot td, table.pay tr.bal td { font-weight: bold; background: #f3efe7; }
  table.bank { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
  table.bank td { border: none; padding: 2px 0; }
  table.bank td:first-child { width: 62mm; color: #333; }
  .content .note { font-size: 9.5pt; margin-top: 10px; }
  @media print { body { background:#fff; } .page { margin:0; box-shadow:none; } }
</style></head><body>
  <div class="page">
    <div class="logo"><img src="${LETTERHEAD_LOGO}" alt="Rohan Corporation logo" onerror="this.style.display='none'"></div>
    <div class="cin">CIN: U45400KA2010PTC052992</div>
    <div class="header-text">
      <h1 class="company-name">Rohan Corporation India Pvt.Ltd</h1>
      <p class="company-sub">(Formerly known as M/s. Property Infra Tech India Pvt Ltd).</p>
    </div>

    <div class="content">
      <div class="row"><div><b>${esc(a.name)}</b><br>${esc(city)}.</div><div>Date: ${esc(today)}</div></div>
      <p>Dear Sir/Madam,</p>
      <div class="sub">Sub: ${esc(a.unit)} in our ${esc((a.project || "").toUpperCase())}.</div>
      <p>Greetings from ${esc(BRAND)}!!</p>
      <p>We are pleased to inform you that the construction work of ${esc((a.project || "our project").toUpperCase())} is going on in full swing and the milestone <b>"${esc(s.label)}"</b> has been completed. We will be thankful to you if you could kindly release the payment in line with the work progress and as per the agreement as follows:</p>
      <table class="pay">
        <tr class="head"><td>Total Cost of Apartment including Car park, GST and Misc. Deposits</td><td class="c"></td><td class="r">${esc(inr(totalCost))}</td></tr>
        ${rows}
        <tr class="tot"><td style="text-align:right">Total</td><td></td><td class="r">${esc(inr(subtotal))}</td></tr>
        <tr><td>Less: Payment received from client upto ${esc(today)}</td><td></td><td class="r">${esc(inr(received))}</td></tr>
        <tr class="bal"><td>Balance Due${pen > 0 ? " (incl. penalty " + esc(inr(pen)) + ")" : ""}</td><td></td><td class="r">${esc(inr(balance + pen))}</td></tr>
      </table>
      <p>Therefore, please arrange to make the above payment by DD/online transfers to our following Bank Account:</p>
      <table class="bank">
        <tr><td>NAME OF THE BENEFICIARY</td><td>${esc(B.beneficiary)}</td></tr>
        <tr><td>NAME OF BANK</td><td>${esc(B.name)}</td></tr>
        <tr><td>ACCOUNT NUMBER</td><td>${esc(B.acc)}</td></tr>
        <tr><td>IFSC CODE</td><td>${esc(B.ifsc)}</td></tr>
        <tr><td>BRANCH NAME</td><td>${esc(B.branch)}</td></tr>
      </table>
      <p>Thanking you,</p>
      <div class="note">Note: Pls do the Payment within the 7 days on Receipt of Demand letter, If Paid, Loan Availed Kindly Ignore the same.</div>
    </div>

    <hr class="footer-rule">
    <div class="footer-text">
      Registered Office: 3-T-19/6(1), Rohan Corporation, Near Capitanio,<br>
      Pumpwell, Mangalore, D.K -575002.&nbsp;&nbsp;Tel: 0824-2431510
    </div>
  </div>
</body></html>`;
}
const COL_COLL = "2fr 1.6fr 1.1fr 0.9fr 1.4fr";
function Collections({ data, plans = [], onPay, onRemind }) {
  const [tab, setTab] = useState("Overdue");
  const [letterView, setLetterView] = useState(null);
  const can = useCan();
  const ui = useUI();
  const rows = [];
  data.forEach((a) => a.schedule.forEach((s, idx) => rows.push({ a, s, idx })));
  const filtered = rows.filter(({ s }) => tab === "All" ? true : tab === "Overdue" ? s.status === "overdue" : tab === "Due" ? s.status === "due" : s.status === "paid");
  const totalOver = rows.filter((r) => r.s.status === "overdue").reduce((x, r) => x + r.s.amt + penaltyOf(r.s), 0);
  const totalDue = rows.filter((r) => r.s.status === "due").reduce((x, r) => x + r.s.amt, 0);
  const totalPaid = rows.filter((r) => r.s.status === "paid").reduce((x, r) => x + r.s.amt, 0);
  const tabCount = (t) => t === "All" ? rows.length : rows.filter((r) => r.s.status === t.toLowerCase()).length;
  const TONE = { Overdue: C.red, Due: C.amber, Paid: C.green, All: C.accent };
  const genLetter = (a, s) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const plan = plans.find((p) => p.id === a.planId) || plans.find((p) => p.name && a.project && p.name.trim().toLowerCase() === a.project.trim().toLowerCase());
    const bank = plan && plan.bank && (plan.bank.beneficiary || plan.bank.acc) ? plan.bank : null;
    w.document.write(demandLetterHtml(a, s, bank));
    w.document.close(); w.focus();
  };
  const letterBank = (a) => {
    const plan = plans.find((p) => p.id === a.planId) || plans.find((p) => p.name && a.project && p.name.trim().toLowerCase() === a.project.trim().toLowerCase());
    return plan && plan.bank && (plan.bank.beneficiary || plan.bank.acc) ? plan.bank : null;
  };
  // Render the demand letter to a real A4 PDF file and download it.
  const downloadLetterPdf = (a, s) => {
    const full = demandLetterHtml(a, s, letterBank(a));
    const style = (full.match(/<style>[\s\S]*?<\/style>/) || [""])[0];
    const page = (full.match(/<div class="page">[\s\S]*<\/div>\s*<\/body>/) || [""])[0].replace(/\s*<\/body>$/, "");
    const holder = document.createElement("div");
    holder.style.cssText = "position:fixed;left:-99999px;top:0;background:#fff";
    holder.innerHTML = style + page;
    document.body.appendChild(holder);
    const el = holder.querySelector(".page");
    if (!el) { holder.remove(); return; }
    el.style.margin = "0";
    el.style.boxShadow = "none";
    return html2pdf().set({
      margin: 0,
      filename: `Demand_${(a.bookingNo || "letter")}.pdf`,
      image: { type: "jpeg", quality: 0.96 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#fff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all"] },
    }).from(el).save().then(() => holder.remove()).catch(() => holder.remove());
  };
  const reminderText = (a, s) => {
    const pen = penaltyOf(s); const tot = s.amt + pen;
    return `Dear ${a.name},\n\nThis is a payment reminder for your booking ${a.bookingNo} (${a.unit}).\n\nMilestone: ${s.label}\nAmount due: ${inr(s.amt)}${pen ? ` + ${inr(pen)} penalty = ${inr(tot)}` : ""}\nDue date: ${s.due}${s.daysLate ? ` (${s.daysLate} days overdue)` : ""}\n\nKindly arrange the payment at the earliest. The formal demand letter is attached.\n\nRegards,\nRohan Corporation`;
  };
  const waOnly = (a, s) => {
    const d = String(a.phone || "").replace(/\D/g, "");
    if (!d) return;
    downloadLetterPdf(a, s); // download PDF to attach (wa.me cannot auto-attach files)
    window.open(`https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(reminderText(a, s))}`, "_blank");
    ui.toast(`WhatsApp opened · letter downloaded`, "info");
  };
  const mailOnly = (a, s) => {
    downloadLetterPdf(a, s); // download PDF to attach (mailto cannot auto-attach files)
    const link = document.createElement("a");
    link.href = `mailto:${a.email || ""}?subject=${encodeURIComponent(`Payment reminder — ${s.label} · ${a.bookingNo}`)}&body=${encodeURIComponent(reminderText(a, s))}`;
    link.click();
    ui.toast(`Email draft opened · letter downloaded`, "info");
  };
  const sendReminder = (a, s, idx) => {
    const text = reminderText(a, s);
    const digits = String(a.phone || "").replace(/\D/g, "");
    const wa = digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(text)}` : null;
    const subject = `Payment reminder — ${s.label} · ${a.bookingNo}`;
    const mail = `mailto:${a.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    genLetter(a, s);                       // letter window — download / print to attach
    if (wa) window.open(wa, "_blank");     // WhatsApp with prefilled message
    const link = document.createElement("a"); link.href = mail; link.click(); // email draft
    onRemind(a.id, idx);                   // mark reminded + log
    ui.toast(`Reminder sent · ${a.name}`);
  };

  return (
    <div>
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(4,minmax(0,1fr))" }}>
        <Kpi label="Overdue (incl. penalty)" value={inr(totalOver)} sub="collect now" tone={C.red} icon={AlertTriangle} />
        <Kpi label="Upcoming due" value={inr(totalDue)} sub="scheduled" tone={C.amber} icon={Calendar} />
        <Kpi label="Collected" value={inr(totalPaid)} sub="received to date" tone={C.green} icon={CheckCircle2} />
        <Kpi label="Penalty rule" value="1.5% / mo" sub="auto-applied on overdue" icon={Wallet} />
      </div>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {["Overdue", "Due", "Paid", "All"].map((t) => { const on = tab === t; const n = tabCount(t); return (
          <button key={t} onClick={() => setTab(t)} className="inline-flex items-center gap-1.5" style={{ fontSize: 12, fontWeight: 500, height: 32, padding: "0 13px", borderRadius: 99, cursor: "pointer", transition: "all .12s", background: on ? C.accent : C.white, color: on ? "#fff" : C.muted, border: `1px solid ${on ? C.accent : C.line}` }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: on ? "rgba(255,255,255,.85)" : TONE[t] }} />
            {t}<span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 6px", borderRadius: 99, background: on ? "rgba(255,255,255,.22)" : C.card, color: on ? "#fff" : C.faint }}>{n}</span>
          </button>
        ); })}
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${C.line}`, background: C.white }}>
        <div className="grid items-center px-5 py-3" style={{ gridTemplateColumns: COL_COLL, background: C.card, borderBottom: `1px solid ${C.line}`, fontSize: 10.5, color: C.muted, fontWeight: 700, letterSpacing: 0.6 }}>
          <div>CUSTOMER</div><div>MILESTONE</div><div>AMOUNT</div><div>STATUS</div><div className="text-right">ACTION</div>
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center" style={{ padding: "56px 0", color: C.faint }}>
            <CheckCircle2 size={28} color={C.green} />
            <div style={{ fontSize: 14, fontWeight: 600, color: C.muted, marginTop: 12 }}>Nothing {tab.toLowerCase()}</div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>No payments in this bucket.</div>
          </div>
        )}
        {filtered.map(({ a, s, idx }, k) => {
          const pen = penaltyOf(s); const last = k === filtered.length - 1;
          return (
            <div key={k} className="grid items-center px-5" style={{ gridTemplateColumns: COL_COLL, background: C.white, borderBottom: last ? "none" : `1px solid ${C.line}`, paddingTop: 13, paddingBottom: 13, transition: "background .12s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.paper)} onMouseLeave={(e) => (e.currentTarget.style.background = C.white)}>
              <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
                <Avatar name={a.name} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</div>
                  <div style={{ fontSize: 11.5, color: C.faint, marginTop: 1 }}><span style={{ fontFamily: "ui-monospace, monospace", color: C.accent }}>{a.bookingNo}</span> · {a.unit}</div>
                </div>
              </div>
              <button type="button" onClick={() => genLetter(a, s)} title="Generate demand letter" className="text-left" style={{ fontSize: 13, minWidth: 0, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                <div className="inline-flex items-center gap-1" style={{ color: C.accent, fontWeight: 500 }}>{s.label}<FileText size={12} /></div>
                <div style={{ fontSize: 11.5, color: s.daysLate ? C.red : C.faint, marginTop: 1 }}>due {s.due}{s.daysLate ? ` · ${s.daysLate}d late` : ""}</div>
              </button>
              <div style={{ fontFamily: display, fontWeight: 600, fontSize: 14.5, color: C.ink }}>{inr(s.amt)}{pen > 0 && <div style={{ fontSize: 11, color: C.red, fontFamily: sans, fontWeight: 500 }}>+{inr(pen)} penalty</div>}</div>
              <div><Pill tone={s.status}>{s.status}</Pill></div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setLetterView({ a, s })} title="Preview demand letter" className="inline-flex items-center gap-1" style={{ fontSize: 12, fontWeight: 500, padding: "6px 10px", borderRadius: 8, cursor: "pointer", background: C.accentSoft, color: C.accent, border: "none" }}><FileText size={12} /> Letter</button>
                {s.status === "paid" ? <span className="inline-flex items-center gap-1" style={{ fontSize: 11.5, color: C.green, fontWeight: 500 }}><Receipt size={13} /> {s.receipt}</span>
                  : !can("operate") ? <span style={{ fontSize: 11.5, color: C.faint }}>view only</span>
                  : (<>
                    <button onClick={() => waOnly(a, s)} title="Send WhatsApp reminder" className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, cursor: "pointer", background: "#25D36622", color: "#1a8d4b", border: "none" }}><MessageCircle size={14} /></button>
                    <button onClick={() => mailOnly(a, s)} title="Send email reminder" className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, cursor: "pointer", background: C.accentSoft, color: C.accent, border: "none" }}><Mail size={14} /></button>
                    {s.status === "overdue" && (s.reminded ? <span className="inline-flex items-center gap-1" style={{ fontSize: 11.5, color: C.amber }}><Bell size={12} /> reminded</span>
                      : <button onClick={() => sendReminder(a, s, idx)} title="Send WhatsApp + email reminder with demand letter" className="inline-flex items-center gap-1" style={{ fontSize: 12, fontWeight: 500, padding: "6px 11px", borderRadius: 8, cursor: "pointer", background: C.amberSoft, color: C.amber, border: "none" }}><Bell size={12} /> Remind</button>)}
                    <button onClick={async () => { if (await ui.confirm({ title: "Record payment", message: `Record ${inr(s.amt)} for "${s.label}" — ${a.name}?\nThis marks the milestone as paid.`, confirmText: "Record payment" })) { onPay(a.id, idx); ui.toast(`Payment recorded · ${a.name}`); } }} style={{ fontSize: 12, fontWeight: 600, padding: "6px 13px", borderRadius: 8, cursor: "pointer", color: "#fff", background: s.status === "overdue" ? C.red : C.accent, border: "none" }}>Record</button>
                  </>)}
              </div>
            </div>);
        })}
      </div>

      {letterView && (
        <div onClick={() => setLetterView(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 12px", overflow: "auto" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(820px,100%)", display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="flex items-center justify-between" style={{ background: C.white, borderRadius: 12, padding: "10px 14px" }}>
              <div style={{ fontFamily: display, fontSize: 15, fontWeight: 600 }}>Demand letter · {letterView.a.name}</div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadLetterPdf(letterView.a, letterView.s)} className="inline-flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Download size={15} /> Download PDF</button>
                <button onClick={() => setLetterView(null)} style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Close</button>
              </div>
            </div>
            <iframe title="Demand letter preview" srcDoc={demandLetterHtml(letterView.a, letterView.s, letterBank(letterView.a))} style={{ width: "100%", height: "75vh", border: "none", borderRadius: 12, background: "#fff" }} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- service ---------------- */
function Service({ data, open }) {
  const rows = [];
  data.forEach((a) => a.service.forEach((s) => rows.push({ a, s })));
  return (
    <Card>
      <H>Service & warranty requests</H>
      {rows.length === 0 ? <div style={{ color: C.faint, fontSize: 13 }}>None.</div> : rows.map(({ a, s }, k) => (
        <div key={k} onClick={() => open(a.id)} className="flex items-center justify-between py-2.5 cursor-pointer" style={{ borderBottom: `1px solid ${C.line}` }}>
          <div><div style={{ fontSize: 13, fontWeight: 500 }}>{s.title}</div><div style={{ fontSize: 11, color: C.faint }}>{a.name} · {a.unit} · {s.date}</div></div>
          <Pill tone={s.status}>{s.status}</Pill>
        </div>))}
    </Card>
  );
}

/* ---------------- tasks ---------------- */
function Tasks({ tasks, onAdd, onToggle }) {
  const [t, setT] = useState("");
  const can = useCan();
  const add = () => { if (!can("operate") || !t.trim()) return; onAdd({ id: Date.now(), text: t, done: false, who: "You" }); setT(""); };
  return (
    <Card>
      <H>Follow-up tasks</H>
      {can("operate") && (
        <div className="flex gap-2 mb-3">
          <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a follow-up…" style={{ flex: 1, outline: "none", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, background: C.white, color: C.ink }} />
          <button onClick={add} className="flex items-center gap-1" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "0 14px", cursor: "pointer", fontSize: 13 }}><Plus size={15} /> Add</button>
        </div>
      )}
      {tasks.map((x) => (
        <div key={x.id} className="flex items-center gap-2.5 py-2" style={{ borderBottom: `1px solid ${C.line}` }}>
          <button disabled={!can("operate")} onClick={() => onToggle(x.id)} style={{ background: "none", border: "none", cursor: can("operate") ? "pointer" : "default", padding: 0 }}>
            {x.done ? <CheckCircle2 size={18} color={C.green} /> : <span style={{ width: 16, height: 16, borderRadius: 99, border: `2px solid ${C.line}`, display: "inline-block" }} />}
          </button>
          <div style={{ fontSize: 13, textDecoration: x.done ? "line-through" : "none", color: x.done ? C.faint : C.ink }}>{x.text}<span style={{ color: C.faint, fontSize: 11 }}> · {x.who}</span></div>
        </div>))}
    </Card>
  );
}

/* ---------------- reports ---------------- */
const MONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function Reports({ data }) {
  const live = data.filter((a) => !a.draft);
  const totalSale = live.reduce((x, a) => x + a.salePrice, 0);
  const collected = live.reduce((x, a) => x + money(a).paid, 0);
  const outstanding = totalSale - collected;
  const collRate = totalSale ? Math.round((collected / totalSale) * 100) : 0;

  // project-wise
  const proj = {};
  live.forEach((a) => { const m = money(a); const p = proj[a.project] || (proj[a.project] = { sale: 0, paid: 0, out: 0, n: 0 }); p.sale += a.salePrice; p.paid += m.paid; p.out += m.outstanding; p.n++; });
  const projRows = Object.entries(proj).map(([name, p]) => ({ name, ...p, pct: p.sale ? Math.round((p.paid / p.sale) * 100) : 0 })).sort((a, b) => b.sale - a.sale);

  // collection-wise (status mix across all instalments)
  let paidTot = 0, dueTot = 0, overTot = 0, penTot = 0;
  live.forEach((a) => a.schedule.forEach((s) => { if (s.status === "paid") paidTot += s.amt; else if (s.status === "due") dueTot += s.amt; else if (s.status === "overdue") { overTot += s.amt; penTot += penaltyOf(s); } }));
  const mix = [
    { k: "Collected", v: paidTot, c: C.green }, { k: "Upcoming due", v: dueTot, c: C.amber },
    { k: "Overdue", v: overTot, c: C.red }, { k: "Penalty accrued", v: penTot, c: C.accent },
  ];

  // month-wise collections (from paid instalment dates)
  const mon = {};
  live.forEach((a) => a.schedule.forEach((s) => { if (s.status === "paid" && s.on && s.on !== "today") { const p = s.on.split(/\s+/); if (p.length === 3 && MONS.includes(p[1])) { const key = p[1] + " " + p[2]; mon[key] = (mon[key] || 0) + s.amt; } } }));
  const monthRows = Object.entries(mon).map(([k, v]) => { const [mn, yr] = k.split(" "); return { k, label: mn + " '" + yr.slice(2), v, ord: Number(yr) * 12 + MONS.indexOf(mn) }; }).sort((a, b) => a.ord - b.ord).slice(-10);

  // salesperson-wise
  const rep = {};
  live.forEach((a) => { const m = money(a); const r = rep[a.salesperson] || (rep[a.salesperson] = { sale: 0, paid: 0, n: 0 }); r.sale += a.salePrice; r.paid += m.paid; r.n++; });
  const repRows = Object.entries(rep).map(([name, r]) => ({ name, ...r })).sort((a, b) => b.paid - a.paid);

  // unit-type-wise
  const typ = {};
  live.forEach((a) => { const m = money(a); const t = typ[a.type] || (typ[a.type] = { sale: 0, out: 0, n: 0 }); t.sale += a.salePrice; t.out += m.outstanding; t.n++; });
  const typeRows = Object.entries(typ).map(([name, t]) => ({ name, ...t })).sort((a, b) => b.sale - a.sale);

  const funnel = STAGES.map((s, i) => ({ s, n: live.filter((a) => a.stageIdx === i).length }));
  const age = (lo, hi) => live.reduce((x, a) => x + a.schedule.filter((s) => s.status === "overdue" && (s.daysLate || 0) > lo && (s.daysLate || 0) <= hi).reduce((y, s) => y + s.amt, 0), 0);
  const ageRows = [{ k: "0–30 days", v: age(-1, 30), c: C.amber }, { k: "31–60 days", v: age(30, 60), c: C.red }, { k: "60+ days", v: live.reduce((x, a) => x + a.schedule.filter((s) => s.status === "overdue" && (s.daysLate || 0) > 60).reduce((y, s) => y + s.amt, 0), 0), c: C.accent }];

  const Export = ({ label }) => (
    <button onClick={() => alert(`${label} exported (demo)`)} className="flex items-center gap-1" style={{ fontSize: 12, padding: "5px 11px", borderRadius: 7, cursor: "pointer", background: C.accentSoft, color: C.accent, border: "none" }}><Download size={13} /> Export</button>
  );

  return (
    <div>
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(4,minmax(0,1fr))" }}>
        <Kpi label="Total sale value" value={inr(totalSale)} sub={`${live.length} active accounts`} icon={Landmark} />
        <Kpi label="Collected" value={inr(collected)} sub="received to date" tone={C.green} icon={Wallet} />
        <Kpi label="Outstanding" value={inr(outstanding)} sub="yet to be received" tone={C.red} icon={Receipt} />
        <Kpi label="Collection rate" value={collRate + "%"} sub="of total sale value" tone={C.accent} icon={BarChart3} />
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div className="flex items-center justify-between"><H>Project-wise collections</H><Export label="Project-wise" /></div>
        <div className="grid items-center px-1 py-2" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1.4fr", fontSize: 10.5, color: C.muted, fontWeight: 700, letterSpacing: 0.5, borderBottom: `1px solid ${C.line}` }}>
          <div>PROJECT</div><div className="text-right">SALE VALUE</div><div className="text-right">COLLECTED</div><div className="text-right">OUTSTANDING</div><div>PROGRESS</div>
        </div>
        {projRows.map((p, i) => (
          <div key={p.name} className="grid items-center px-1 py-2.5" style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1.4fr", fontSize: 13, borderBottom: i === projRows.length - 1 ? "none" : `1px solid ${C.line}` }}>
            <div style={{ fontWeight: 600 }}>{p.name}<span style={{ fontSize: 11, color: C.faint, fontWeight: 400 }}> · {p.n}</span></div>
            <div className="text-right" style={{ fontFamily: display, fontWeight: 600 }}>{inr(p.sale)}</div>
            <div className="text-right" style={{ fontFamily: display, fontWeight: 600, color: C.green }}>{inr(p.paid)}</div>
            <div className="text-right" style={{ fontFamily: display, fontWeight: 600, color: p.out > 0 ? C.red : C.green }}>{inr(p.out)}</div>
            <div className="flex items-center gap-2"><div style={{ flex: 1 }}><Pbar pct={p.pct} /></div><span style={{ fontSize: 11.5, color: C.muted, fontWeight: 600, width: 32, textAlign: "right" }}>{p.pct}%</span></div>
          </div>
        ))}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div className="flex items-center justify-between mb-2"><H>Month-wise collections</H><Export label="Month-wise" /></div>
        {monthRows.length === 0 ? <div style={{ fontSize: 13, color: C.faint, padding: "20px 0" }}>No recorded payments yet.</div> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthRows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} axisLine={{ stroke: C.line }} tickLine={false} />
              <Tooltip cursor={{ fill: C.card }} formatter={(v) => [inr(v), "Collected"]} contentStyle={{ borderRadius: 10, border: `1px solid ${C.line}`, fontSize: 12 }} />
              <Bar dataKey="v" radius={[6, 6, 0, 0]} fill={C.accent} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <Card><div className="flex items-center justify-between"><H>Collection status mix</H><Export label="Status mix" /></div>
          {mix.map((m, i) => <div key={m.k} className="flex items-center justify-between py-2.5" style={{ borderBottom: i === mix.length - 1 ? "none" : `1px solid ${C.line}` }}><span className="flex items-center gap-2" style={{ fontSize: 13, color: C.muted }}><span style={{ width: 9, height: 9, borderRadius: 99, background: m.c }} />{m.k}</span><span style={{ fontFamily: display, fontWeight: 600, color: m.c }}>{inr(m.v)}</span></div>)}
        </Card>

        <Card><div className="flex items-center justify-between"><H>Salesperson-wise</H><Export label="Salesperson" /></div>
          {repRows.map((r, i) => <div key={r.name} className="flex items-center justify-between py-2.5" style={{ borderBottom: i === repRows.length - 1 ? "none" : `1px solid ${C.line}` }}><span style={{ fontSize: 13, color: C.muted }}>{r.name}<span style={{ fontSize: 11, color: C.faint }}> · {r.n} account{r.n !== 1 ? "s" : ""}</span></span><span style={{ fontFamily: display, fontWeight: 600, color: C.green }}>{inr(r.paid)}</span></div>)}
        </Card>

        <Card><div className="flex items-center justify-between"><H>Unit-type-wise</H><Export label="Unit type" /></div>
          {typeRows.map((t, i) => <div key={t.name} className="flex items-center justify-between py-2.5" style={{ borderBottom: i === typeRows.length - 1 ? "none" : `1px solid ${C.line}` }}><span style={{ fontSize: 13, color: C.muted }}>{t.name}<span style={{ fontSize: 11, color: C.faint }}> · {t.n}</span></span><span style={{ fontFamily: display, fontWeight: 600 }}>{inr(t.sale)}<span style={{ fontSize: 11, color: C.red, fontFamily: sans, fontWeight: 500 }}> · {inr(t.out)} due</span></span></div>)}
        </Card>

        <Card><div className="flex items-center justify-between"><H>Stage funnel</H><Export label="Funnel" /></div>
          {funnel.map((f, i) => <Row key={f.s} k={f.s} v={`${f.n} account${f.n !== 1 ? "s" : ""}`} last={i === funnel.length - 1} />)}
        </Card>

        <Card><div className="flex items-center justify-between"><H>Dues ageing</H><Export label="Ageing" /></div>
          {ageRows.map((r, i) => <div key={r.k} className="flex items-center justify-between py-2.5" style={{ borderBottom: i === ageRows.length - 1 ? "none" : `1px solid ${C.line}` }}><span className="flex items-center gap-2" style={{ fontSize: 13, color: C.muted }}><span style={{ width: 9, height: 9, borderRadius: 99, background: r.c }} />{r.k}</span><span style={{ fontFamily: display, fontWeight: 600, color: r.v > 0 ? r.c : C.ink }}>{inr(r.v)}</span></div>)}
        </Card>

        <Card><div className="flex items-center justify-between"><H>Collections summary</H><Export label="Summary" /></div>
          <Row k="Total sale value" v={inr(totalSale)} /><Row k="Collected" v={inr(collected)} tone={C.green} /><Row k="Outstanding" v={inr(outstanding)} tone={C.red} /><Row k="Collection rate" v={collRate + "%"} tone={C.accent} last />
        </Card>
      </div>
    </div>
  );
}
function Row({ k, v, tone, last }) {
  return <div className="flex items-center justify-between py-2.5" style={{ borderBottom: last ? "none" : `1px solid ${C.line}` }}><span style={{ fontSize: 13, color: C.muted }}>{k}</span><span style={{ fontFamily: display, fontWeight: 600, color: tone || C.ink }}>{v}</span></div>;
}

/* ---------------- customer portal preview ---------------- */
function Portal({ acc }) {
  const m = money(acc);
  return (
    <div>
      <div className="flex items-center gap-2 mb-3" style={{ fontSize: 12, color: C.muted }}><Smartphone size={14} /> What the buyer sees when they log in (read-only preview)</div>
      <div style={{ maxWidth: 420, margin: "0 auto" }}>
        <Card style={{ background: C.accent, border: "none", color: "#fff" }} pad={5}>
          <div style={{ fontSize: 12, opacity: 0.8 }}>Welcome back,</div>
          <div style={{ fontFamily: display, fontSize: 22, fontWeight: 600 }}>{acc.name}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>{acc.project} · {acc.unit}</div>
          <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
            <div><div style={{ fontSize: 11, opacity: 0.8 }}>Outstanding</div><div style={{ fontFamily: display, fontSize: 20, fontWeight: 600 }}>{inr(m.outstanding)}</div></div>
            <button style={{ background: "#fff", color: C.accent, border: "none", borderRadius: 8, padding: "9px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Pay now</button>
          </div>
        </Card>
        <Card style={{ marginTop: 14 }}><H>My payment schedule</H>
          {acc.schedule.map((s, i) => <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: i < acc.schedule.length - 1 ? `1px solid ${C.line}` : "none" }}><div style={{ fontSize: 13 }}>{s.label}<div style={{ fontSize: 11, color: C.faint }}>due {s.due}</div></div><Pill tone={s.status}>{s.status}</Pill></div>)}
        </Card>
        <Card style={{ marginTop: 14 }}>
          <div className="flex items-center justify-between"><H>Documents & support</H></div>
          <div className="flex gap-2"><button className="flex items-center gap-1.5" style={{ flex: 1, justifyContent: "center", background: C.accentSoft, color: C.accent, border: "none", borderRadius: 8, padding: "10px", fontSize: 13, cursor: "pointer" }}><FileText size={14} /> My documents</button><button className="flex items-center gap-1.5" style={{ flex: 1, justifyContent: "center", background: C.accentSoft, color: C.accent, border: "none", borderRadius: 8, padding: "10px", fontSize: 13, cursor: "pointer" }}><Wrench size={14} /> Raise a request</button></div>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Ask AI (preview) ---------------- */
const AI_HISTORY_STORE = "rc_ai_history";
const AI_PREVIEW_REPLY = "This is a preview of the Ask AI assistant — responses aren't connected to a live model yet.";
const genId = () => Date.now() + Math.floor(Math.random() * 1000);

const SAMPLE_CHATS = [
  { id: 9001, title: "Payment reminder — Ananya Rao", messages: [
    { role: "user", content: "Draft a payment reminder for an overdue installment" },
    { role: "assistant", content: AI_REPLIES["Draft a payment reminder for an overdue installment"] },
  ] },
  { id: 9002, title: "Stamp duty explained", messages: [
    { role: "user", content: "Explain stamp duty and registration charges simply" },
    { role: "assistant", content: AI_REPLIES["Explain stamp duty and registration charges simply"] },
  ] },
  { id: 9003, title: "Plot registration documents", messages: [
    { role: "user", content: "List documents needed for plot registration" },
    { role: "assistant", content: AI_REPLIES["List documents needed for plot registration"] },
  ] },
  { id: 9004, title: "Possession handover note", messages: [
    { role: "user", content: "Write a possession handover message to a customer" },
    { role: "assistant", content: AI_REPLIES["Write a possession handover message to a customer"] },
  ] },
];
function AskAI() {
  const [conversations, setConversations] = useState(() => {
    try { const raw = localStorage.getItem(AI_HISTORY_STORE); if (raw != null) return JSON.parse(raw) || []; } catch { /* ignore */ }
    return SAMPLE_CHATS;
  });
  const [activeId, setActiveId] = useState(null);
  const [input, setInput] = useState("");
  const scroller = useRef(null);

  const active = conversations.find((c) => c.id === activeId);
  const messages = active ? active.messages : [];

  useEffect(() => {
    try { localStorage.setItem(AI_HISTORY_STORE, JSON.stringify(conversations)); } catch { /* ignore */ }
  }, [conversations]);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages]);

  const ask = (q) => {
    const text = (q || "").trim();
    if (!text) return;
    let id = activeId;
    let convos = conversations;
    if (id == null || !convos.find((c) => c.id === id)) {
      id = genId();
      convos = [{ id, title: text.slice(0, 32), messages: [] }, ...convos];
    }
    setConversations(convos.map((c) => c.id === id ? {
      ...c,
      title: c.messages.length === 0 ? text.slice(0, 32) : c.title,
      messages: [...c.messages, { role: "user", content: text }, { role: "assistant", content: replyFor(text) }],
    } : c));
    setActiveId(id);
    setInput("");
  };

  const newChat = () => { setActiveId(null); setInput(""); };
  const deleteChat = (id) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (id === activeId) setActiveId(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(input); }
  };

  return (
    <Card pad={0} style={{ display: "flex", height: "100%", minHeight: 520, overflow: "hidden" }}>
      {/* history */}
      <div className="flex flex-col" style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${C.line}`, background: C.paper }}>
        <div className="px-3 py-3">
          <button onClick={newChat} className="flex items-center justify-center gap-1.5 w-full rounded-lg" style={{ background: C.accent, color: "#fff", border: "none", height: 36, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={15} /> New chat
          </button>
        </div>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: C.faint, letterSpacing: 0.4, padding: "2px 14px 6px", textTransform: "uppercase" }}>History</div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 8px" }}>
          {conversations.length === 0 && (
            <div style={{ fontSize: 12, color: C.faint, padding: "6px" }}>No chats yet.</div>
          )}
          {conversations.map((c) => {
            const on = c.id === activeId;
            return (
              <div key={c.id} onClick={() => setActiveId(c.id)} className="flex items-center gap-1.5" style={{ cursor: "pointer", borderRadius: 9, padding: "8px 9px", marginBottom: 2, background: on ? C.white : "transparent", border: `1px solid ${on ? C.line : "transparent"}` }}>
                <FileText size={13} color={C.faint} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title || "New chat"}</span>
                <button onClick={(e) => { e.stopPropagation(); deleteChat(c.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.faint, display: "flex", flexShrink: 0, padding: 2 }}>
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* chat */}
      <div className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
        <div className="flex items-center gap-2.5 px-5 py-4" style={{ borderBottom: `1px solid ${C.line}` }}>
          <div className="flex items-center justify-center rounded-full" style={{ width: 34, height: 34, background: C.accentSoft, color: C.accent, flexShrink: 0 }}><Sparkles size={17} /></div>
          <div>
            <div style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Ask AI</div>
            <div style={{ fontSize: 11, color: C.faint }}>General assistant</div>
          </div>
          <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, color: C.amber, background: C.amberSoft, borderRadius: 99, padding: "3px 10px" }}>Preview</span>
        </div>

        <div ref={scroller} className="px-5 py-4" style={{ flex: 1, overflowY: "auto" }}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center" style={{ height: "100%", color: C.faint }}>
              <Sparkles size={28} color={C.lime} />
              <div style={{ marginTop: 10, fontSize: 14, color: C.muted, fontWeight: 500 }}>How can I help?</div>
              <div style={{ marginTop: 4, marginBottom: 16, fontSize: 12, maxWidth: 360 }}>Pick a suggestion to get started.</div>
              <div className="flex flex-col gap-2" style={{ width: "100%", maxWidth: 420 }}>
                {AI_SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => ask(s)} className="flex items-center gap-2" style={{ textAlign: "left", background: C.white, border: `1px solid ${C.line}`, borderRadius: 12, padding: "11px 14px", fontSize: 13, color: C.ink, cursor: "pointer", fontFamily: sans }}>
                    <Sparkles size={14} color={C.accent} /> {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className="flex" style={{ justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
                <div style={{
                  maxWidth: "78%", whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.5,
                  padding: "10px 13px", borderRadius: 14,
                  background: m.role === "user" ? C.accent : C.card,
                  color: m.role === "user" ? "#fff" : C.ink,
                  border: m.role === "user" ? "none" : `1px solid ${C.line}`,
                }}>{m.content}</div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-end gap-2 px-4 py-3" style={{ borderTop: `1px solid ${C.line}` }}>
          <textarea
            value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
            placeholder="Type a message…" rows={1}
            style={{ flex: 1, resize: "none", outline: "none", border: `1px solid ${C.line}`, borderRadius: 12, padding: "10px 12px", fontFamily: sans, fontSize: 13.5, color: C.ink, background: C.white, maxHeight: 120 }}
          />
          <button onClick={() => ask(input)} disabled={!input.trim()} className="flex items-center justify-center rounded-full" style={{ width: 40, height: 40, flexShrink: 0, background: !input.trim() ? C.faint : C.accent, color: "#fff", border: "none", cursor: !input.trim() ? "default" : "pointer" }}>
            <Send size={16} />
          </button>
        </div>
      </div>
    </Card>
  );
}

/* ---------------- milestones / payment plans ---------------- */
function planPrintHtml(plan, saleValue) {
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const ms = plan.milestones || [];
  const sum = ms.reduce((x, m) => x + (Number(m.pct) || 0), 0);
  const val = Number(saleValue) || 0;
  const amtCol = val > 0;
  const rows = ms.map((m, i) => {
    const same = i > 0 && ms[i - 1].stage === m.stage;
    return `<tr><td class="c">${i + 1}</td><td class="st">${same ? "" : esc(m.stage)}</td><td>${esc(m.label)}</td><td class="c">${m.pct}%</td>${amtCol ? `<td class="r">${esc(inr(Math.round(val * (Number(m.pct) || 0) / 100)))}</td>` : ""}</tr>`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(plan.name)} — Payment Schedule</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#2b2b2b;margin:32px}
  h1{font-size:22px;margin:0;text-align:center;color:#6E4B1E}
  .sub{text-align:center;font-size:12px;color:#666;margin:4px 0 2px}
  table{width:100%;border-collapse:collapse;margin-top:18px;font-size:12px}
  th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;vertical-align:top}
  th{background:#6E4B1E;color:#fff;font-size:11px}
  td.c{text-align:center;width:42px} td.r{text-align:right;white-space:nowrap} td.st{font-weight:bold;width:170px}
  tr.tot td{background:#f3efe7;font-weight:bold}
  .notes{font-size:10.5px;color:#555;margin-top:16px} .notes li{margin-bottom:3px}
  @media print{body{margin:12mm}}
</style></head><body>
  <h1>${esc(plan.name.toUpperCase())}</h1>
  <div class="sub">Construction-Linked Payment Schedule${plan.location ? " | " + esc(plan.location) : ""}</div>
  ${plan.rera ? `<div class="sub">RERA No.: ${esc(plan.rera)}</div>` : ""}
  ${amtCol ? `<div class="sub">Total Consideration: ${esc(inr(val))}</div>` : ""}
  <table>
    <thead><tr><th>Sr.</th><th>Construction Stage</th><th>Payment Milestone</th><th>% of Total</th>${amtCol ? "<th>Amount</th>" : ""}</tr></thead>
    <tbody>${rows}
      <tr class="tot"><td></td><td></td><td style="text-align:right">TOTAL</td><td class="c">${sum}%</td>${amtCol ? `<td class="r">${esc(inr(Math.round(val * sum / 100)))}</td>` : ""}</tr>
    </tbody>
  </table>
  <ol class="notes">
    <li>Indicative construction-linked payment (CLP) schedule. Amounts are base sale consideration only.</li>
    <li>Payment % is fixed per milestone; the rupee amount scales with each buyer's agreed sale value.</li>
    <li>GST, stamp duty, registration charges and statutory levies are extra.</li>
    <li>Each instalment is due within 15 days of the developer's intimation that the milestone is achieved.</li>
    <li>Schedule and amounts are tentative and subject to the Agreement for Sale. Not a legal offering.</li>
  </ol>
</body></html>`;
}
function Milestones({ plans, onSave, onDelete, onMilestoneDone }) {
  const can = useCan();
  const ui = useUI();
  const canEdit = can("manage");
  const isAdmin = can("admin");                 // only Super Admin can mark milestones finished
  const [selId, setSelId] = useState(plans[0] ? plans[0].id : null);
  const [adding, setAdding] = useState(false);
  const [np, setNp] = useState({ name: "", location: "", rera: "" });
  const [preview, setPreview] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const saved = plans.find((p) => p.id === selId) || plans[0] || null;
  const cur = editing ? draft : saved;          // what is displayed
  const ms = cur ? cur.milestones || [] : [];
  const sum = ms.reduce((x, m) => x + (Number(m.pct) || 0), 0);
  const ok = Math.abs(sum - 100) < 0.01;
  const pv = Number(preview) || 0;

  const pick = (id) => { setEditing(false); setDraft(null); setSelId(id); };
  const newId = () => (plans.length ? Math.max(...plans.map((p) => p.id)) : 0) + 1;
  const addProject = () => {
    if (!np.name.trim()) return;
    const p = { id: newId(), name: np.name.trim(), location: np.location.trim(), rera: np.rera.trim(), milestones: [] };
    onSave(p); setSelId(p.id); setNp({ name: "", location: "", rera: "" }); setAdding(false);
    setDraft(JSON.parse(JSON.stringify(p))); setEditing(true);
  };
  // Editing acts on a local draft; nothing is saved until Save.
  const startEdit = () => { setDraft(JSON.parse(JSON.stringify(saved))); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); };
  const saveEdit = () => { onSave(draft); setEditing(false); setDraft(null); };
  const dPatch = (ch) => setDraft((d) => ({ ...d, ...ch }));
  const dSetMs = (list) => setDraft((d) => ({ ...d, milestones: list }));
  const dUpdMs = (i, key, val) => dSetMs(ms.map((m, idx) => idx === i ? { ...m, [key]: key === "pct" ? (val === "" ? "" : Number(val)) : val } : m));
  // Super-admin marks a milestone finished. In view mode it persists at once; while editing it goes to the draft.
  const toggleDone = (i) => {
    if (!isAdmin) return;
    const base = editing ? draft : saved;
    if (!base) return;
    const m = (base.milestones || [])[i];
    const nextDone = !(m && m.done);
    const list = (base.milestones || []).map((mm, idx) => idx === i ? { ...mm, done: nextDone } : mm);
    if (editing) { dSetMs(list); return; }     // staged in draft; demand syncs on view-mode toggle
    onSave({ ...base, milestones: list });
    if (onMilestoneDone && m) onMilestoneDone(base, m, nextDone);   // push demand into Collections
  };
  const doneN = ms.filter((m) => m.done).length;
  const printPlan = () => {
    if (!cur) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(planPrintHtml(cur, pv));
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };
  const cell = { outline: "none", border: `1px solid ${C.line}`, borderRadius: 7, padding: "6px 8px", fontSize: 12.5, background: C.white, color: C.ink, width: "100%", fontFamily: sans };
  const gc = pv > 0 ? "30px 28px 1.1fr 2fr 70px 1fr 32px" : "30px 28px 1.1fr 2fr 70px 32px";

  return (
    <div className="grid gap-4 items-start" style={{ gridTemplateColumns: "260px 1fr" }}>
      <Card pad={4}>
        <div className="flex items-center justify-between mb-2"><H>Projects</H>{canEdit && <button onClick={() => setAdding(!adding)} title="Add project" style={{ background: C.accentSoft, color: C.accent, border: "none", borderRadius: 7, padding: 6, cursor: "pointer" }}><Plus size={15} /></button>}</div>
        {plans.map((p) => {
          const psum = (p.milestones || []).reduce((x, m) => x + (Number(m.pct) || 0), 0);
          const pdone = (p.milestones || []).filter((m) => m.done).length;
          const active = saved && p.id === saved.id;
          return (
            <button key={p.id} onClick={() => pick(p.id)} className="w-full text-left" style={{ display: "block", background: active ? C.accentSoft : "transparent", border: "none", borderRadius: 9, padding: "9px 10px", cursor: "pointer", marginBottom: 2 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: active ? C.accent : C.ink }}>{p.name}</div>
              <div style={{ fontSize: 11, color: C.faint }}>{(p.milestones || []).length} milestones · {psum}%{pdone > 0 && <span style={{ color: C.green, fontWeight: 600 }}> · {pdone} done</span>}</div>
            </button>);
        })}
        {adding && (
          <div className="mt-3 pt-3 flex flex-col gap-2" style={{ borderTop: `1px solid ${C.line}` }}>
            <input value={np.name} onChange={(e) => setNp({ ...np, name: e.target.value })} placeholder="Project name" style={cell} />
            <input value={np.location} onChange={(e) => setNp({ ...np, location: e.target.value })} placeholder="Location" style={cell} />
            <input value={np.rera} onChange={(e) => setNp({ ...np, rera: e.target.value })} placeholder="RERA no." style={cell} />
            <button onClick={addProject} style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Add project</button>
          </div>
        )}
      </Card>

      {!cur ? <Card>No plans yet. Add a project.</Card> : (
        <Card>
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div style={{ minWidth: 0 }}>
              {editing
                ? <input value={cur.name} onChange={(e) => dPatch({ name: e.target.value })} style={{ ...cell, fontSize: 18, fontWeight: 600, fontFamily: display, width: 280 }} />
                : <div style={{ fontFamily: display, fontSize: 20, fontWeight: 600 }}>{cur.name}</div>}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap" style={{ fontSize: 12, color: C.muted }}>
                {editing ? <input value={cur.location || ""} onChange={(e) => dPatch({ location: e.target.value })} placeholder="Location" style={{ ...cell, width: 220 }} /> : <span>{cur.location}</span>}
                {editing ? <input value={cur.rera || ""} onChange={(e) => dPatch({ rera: e.target.value })} placeholder="RERA no." style={{ ...cell, width: 320 }} /> : <span>RERA: {cur.rera}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: 12, fontWeight: 600, color: ok ? C.green : C.red, background: ok ? C.greenSoft : C.redSoft, borderRadius: 99, padding: "5px 11px" }}>{ok ? "✓ totals 100%" : `total ${sum}% (must be 100%)`}</span>
              {!editing && <button onClick={printPlan} className="flex items-center gap-1.5" style={{ background: C.accentSoft, color: C.accent, border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Download size={14} /> Print{pv > 0 ? " (with amounts)" : ""}</button>}
              {canEdit && !editing && <>
                <button onClick={startEdit} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Save size={14} /> Edit</button>
                <button onClick={async () => { if (await ui.confirm({ title: "Delete plan", message: `Delete the milestone plan "${saved.name}"? This cannot be undone.`, confirmText: "Delete", danger: true })) { onDelete(saved.id); setSelId(null); ui.toast("Plan deleted", "warn"); } }} title="Delete plan" style={{ background: "transparent", color: C.red, border: `1px solid ${C.line}`, borderRadius: 8, padding: 8, cursor: "pointer" }}><Trash2 size={15} /></button>
              </>}
              {editing && <>
                <button onClick={cancelEdit} style={{ background: C.white, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                <button onClick={saveEdit} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}><Save size={14} /> Save</button>
              </>}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3" style={{ fontSize: 12, color: C.muted }}>
            <span>Preview amounts for sale value ₹</span>
            <input value={preview} onChange={(e) => setPreview(e.target.value.replace(/[^0-9]/g, ""))} placeholder="e.g. 10000000" style={{ ...cell, width: 160 }} />
            {pv > 0 && <span style={{ color: C.accent }}>each instalment = % × {inr(pv)}</span>}
          </div>

          {(() => {
            const bk = cur.bank || {};
            const dBank = (k, v) => dPatch({ bank: { ...(cur.bank || {}), [k]: v } });
            const hasBank = bk.beneficiary || bk.name || bk.acc || bk.ifsc || bk.branch;
            return (
              <div className="rounded-lg p-3 mb-3" style={{ border: `1px solid ${C.line}`, background: C.card }}>
                <div className="flex items-center gap-2 mb-2.5"><Landmark size={14} color={C.accent} /><span style={{ fontSize: 13, fontWeight: 600 }}>Bank account — used in demand letters for this project</span></div>
                {editing ? (
                  <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
                    <input value={bk.beneficiary || ""} onChange={(e) => dBank("beneficiary", e.target.value)} placeholder="Name of beneficiary" style={{ ...cell, gridColumn: "span 2" }} />
                    <input value={bk.name || ""} onChange={(e) => dBank("name", e.target.value)} placeholder="Name of bank" style={cell} />
                    <input value={bk.acc || ""} onChange={(e) => dBank("acc", e.target.value)} placeholder="Account number" style={cell} />
                    <input value={bk.ifsc || ""} onChange={(e) => dBank("ifsc", e.target.value)} placeholder="IFSC code" style={cell} />
                    <input value={bk.branch || ""} onChange={(e) => dBank("branch", e.target.value)} placeholder="Branch name" style={cell} />
                  </div>
                ) : hasBank ? (
                  <div style={{ fontSize: 12.5 }}>
                    {[["Beneficiary", bk.beneficiary], ["Bank", bk.name], ["Account no.", bk.acc], ["IFSC", bk.ifsc], ["Branch", bk.branch]].map(([k, v]) => (
                      <div key={k} className="flex gap-3 py-0.5"><span style={{ width: 110, color: C.muted, flexShrink: 0 }}>{k}</span><span style={{ color: C.ink, fontFamily: k === "Account no." || k === "IFSC" ? "ui-monospace, monospace" : undefined }}>{v || "—"}</span></div>
                    ))}
                  </div>
                ) : <div style={{ fontSize: 12.5, color: C.faint }}>No bank account added. {canEdit ? "Click Edit to add one." : ""}</div>}
              </div>
            );
          })()}

          {(isAdmin || doneN > 0) && (
            <div className="flex items-center gap-2 mb-2.5" style={{ fontSize: 12, color: C.muted }}>
              <CheckCircle2 size={14} color={C.green} />
              <span style={{ fontWeight: 600, color: doneN ? C.green : C.muted }}>{doneN}/{ms.length} finished</span>
              {isAdmin && <span style={{ color: C.faint }}>· tap the circle to mark a milestone complete</span>}
            </div>
          )}
          <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
            <div className="grid items-center px-3 py-2" style={{ gridTemplateColumns: gc, background: C.card, fontSize: 11, color: C.muted, fontWeight: 600, gap: 8 }}>
              <div /><div>#</div><div>STAGE</div><div>MILESTONE</div><div>%</div>{pv > 0 && <div className="text-right">AMOUNT</div>}<div />
            </div>
            {ms.map((m, i) => {
              const sameStage = i > 0 && ms[i - 1].stage === m.stage;
              return (
                <div key={i} className="grid items-center px-3 py-1.5" style={{ gridTemplateColumns: gc, borderTop: `1px solid ${C.line}`, fontSize: 12.5, gap: 8, background: m.done ? C.greenSoft : "transparent" }}>
                  <div className="flex items-center justify-center">
                    {isAdmin
                      ? <button onClick={() => toggleDone(i)} title={m.done ? "Mark not finished" : "Mark finished"} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", lineHeight: 0 }}>
                          {m.done ? <CheckCircle2 size={18} color={C.green} /> : <span style={{ width: 16, height: 16, borderRadius: 99, border: `2px solid ${C.line}`, display: "inline-block" }} />}
                        </button>
                      : (m.done ? <CheckCircle2 size={16} color={C.green} /> : <span style={{ width: 14, height: 14, borderRadius: 99, border: `2px solid ${C.line}`, display: "inline-block", opacity: 0.5 }} />)}
                  </div>
                  <div style={{ color: C.faint }}>{i + 1}</div>
                  {editing
                    ? <input value={m.stage || ""} onChange={(e) => dUpdMs(i, "stage", e.target.value)} style={cell} />
                    : <div style={{ color: sameStage ? "transparent" : C.muted, fontWeight: 600 }}>{m.stage}</div>}
                  {editing ? <input value={m.label || ""} onChange={(e) => dUpdMs(i, "label", e.target.value)} style={cell} /> : <div style={{ color: m.done ? C.green : C.ink }}>{m.label}</div>}
                  {editing ? <input value={m.pct} onChange={(e) => dUpdMs(i, "pct", e.target.value)} style={{ ...cell, textAlign: "right" }} /> : <div>{m.pct}%</div>}
                  {pv > 0 && <div className="text-right" style={{ fontFamily: display, fontWeight: 600 }}>{inr(Math.round(pv * (Number(m.pct) || 0) / 100))}</div>}
                  <div className="text-right">{editing && <button onClick={() => dSetMs(ms.filter((_, idx) => idx !== i))} title="Remove" style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", padding: 2 }}><Trash2 size={13} /></button>}</div>
                </div>);
            })}
            <div className="grid items-center px-3 py-2" style={{ gridTemplateColumns: gc, borderTop: `2px solid ${C.line}`, background: C.card, fontSize: 12.5, fontWeight: 700, gap: 8 }}>
              <div /><div /><div /><div className="text-right">TOTAL</div><div style={{ color: ok ? C.green : C.red }}>{sum}%</div>{pv > 0 && <div className="text-right" style={{ fontFamily: display }}>{inr(Math.round(pv * sum / 100))}</div>}<div />
            </div>
          </div>
          {editing && <button onClick={() => dSetMs([...ms, { stage: ms.length ? ms[ms.length - 1].stage : "", label: "", pct: 0, done: false }])} className="flex items-center gap-1.5 mt-3" style={{ fontSize: 13, color: C.accent, background: C.accentSoft, border: "none", borderRadius: 8, padding: "8px 13px", cursor: "pointer", fontWeight: 600 }}><Plus size={15} /> Add milestone</button>}
        </Card>
      )}
    </div>
  );
}

/* ---------------- settings ---------------- */
function SettingsView({ role: myRole, setRole: setMyRole }) {
  const can = useCan();
  const isAdmin = can("admin");
  const [users, setUsers] = useState(SEED_USERS);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [newRole, setNewRole] = useState(ROLES[2].name);

  const [org, setOrg] = useState({ name: "Rohan Corporation", gstin: "29ABCDE1234F1Z5", address: "MG Road, Bengaluru 560001", currency: "INR (₹)" });
  const [notif, setNotif] = useState({ overdue: true, receipts: true, possession: true, service: false });
  const [coll, setColl] = useState({ penalty: "1.5", grace: "7", autoRemind: true });
  const [appr, setAppr] = useState({ theme: "Light", dateFmt: "DD MMM YYYY", rows: "25" });

  const addUser = () => {
    if (!isAdmin || !name.trim()) return;
    setUsers([...users, { name: name.trim(), email: email.trim(), role: newRole }]);
    setName(""); setEmail(""); setNewRole(ROLES[2].name);
  };
  const removeUser = (idx) => { if (isAdmin) setUsers(users.filter((_, i) => i !== idx)); };

  const inputStyle = { outline: "none", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, background: C.white, color: C.ink, fontFamily: sans };

  const [tab, setTab] = useState("Organization");
  const tabs = ["Organization", "Users & roles", "Notifications", "Collections", "Appearance", "Audit log"];
  const orgInit = (org.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const myMeta = roleMeta(myRole);

  return (
    <div className="grid gap-5" style={{ gridTemplateColumns: "300px minmax(0,1fr)", alignItems: "start" }}>
      {/* sticky organization rail */}
      <div style={{ position: "sticky", top: 0 }}>
        <Card pad={5}>
          <div className="flex items-center gap-3" style={{ marginBottom: 14 }}>
            <div className="flex items-center justify-center rounded-full" style={{ width: 48, height: 48, flexShrink: 0, background: C.accent, color: "#fff", fontFamily: display, fontSize: 18, fontWeight: 600 }}>{orgInit}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: display, fontSize: 19, fontWeight: 600, lineHeight: 1.15 }}>{org.name}</div>
              <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, color: C.accent, marginTop: 2 }}>{org.gstin}</div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5" style={{ fontSize: 12.5, color: C.muted, marginBottom: 14 }}>
            <span className="flex items-center gap-2"><MapPin size={13} color={C.accent} /> {org.address}</span>
            <span className="flex items-center gap-2"><Wallet size={13} color={C.accent} /> {org.currency}</span>
          </div>

          <div className="rounded-lg p-3" style={{ background: C.card, border: `1px solid ${C.line}`, marginBottom: 14 }}>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 6 }}>Active role (preview)</div>
            <BrandSelect value={myRole} onChange={setMyRole} options={ROLES.map((r) => r.name)} />
            <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>{myMeta.access}</div>
          </div>

          <div>
            <MoneyRow label="Team members" value={String(users.length)} strong />
            <MoneyRow label="Roles" value={String(ROLES.length)} />
            <div className="flex items-center justify-between" style={{ padding: "7px 0" }}>
              <span style={{ fontSize: 12, color: C.muted }}>Your access</span>
              <Pill tone={myMeta.tone}>{myRole}</Pill>
            </div>
          </div>
        </Card>
      </div>

      {/* tabbed settings content */}
      <div style={{ minWidth: 0 }}>
        <div className="flex items-center gap-1 flex-wrap" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: 4, marginBottom: 18 }}>
          {tabs.map((t) => <DetailTab key={t} label={t} active={tab === t} onClick={() => setTab(t)} />)}
        </div>

        {tab === "Organization" && (
          <Card>
            <div className="flex items-center gap-2 mb-3"><Building2 size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Organization</span></div>
            {!isAdmin && <div style={{ fontSize: 12, color: C.faint, marginBottom: 10 }}>Only a Super Admin can change organization settings.</div>}
            <div className="grid gap-x-6 gap-y-3" style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
              <SField label="Company name"><input value={org.name} disabled={!isAdmin} onChange={(e) => setOrg({ ...org, name: e.target.value })} style={fieldStyle(isAdmin)} /></SField>
              <SField label="GSTIN"><input value={org.gstin} disabled={!isAdmin} onChange={(e) => setOrg({ ...org, gstin: e.target.value })} style={fieldStyle(isAdmin)} /></SField>
              <SField label="Registered address"><input value={org.address} disabled={!isAdmin} onChange={(e) => setOrg({ ...org, address: e.target.value })} style={fieldStyle(isAdmin)} /></SField>
              <SField label="Default currency"><BrandSelect value={org.currency} enabled={isAdmin} onChange={(v) => setOrg({ ...org, currency: v })} options={["INR (₹)", "USD ($)", "AED (د.إ)"]} /></SField>
            </div>
          </Card>
        )}

        {tab === "Users & roles" && (
          <div className="flex flex-col gap-5">
            <Card>
              <div className="flex items-center gap-2 mb-3"><Users size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Users & roles</span></div>
              {!isAdmin && <div style={{ fontSize: 12, color: C.faint, marginBottom: 8 }}>Only a Super Admin can add or remove users.</div>}
              {users.map((u, i) => {
                const meta = roleMeta(u.role);
                return (
                  <div key={i} className="flex items-center justify-between gap-3 py-2.5" style={{ borderBottom: i < users.length - 1 ? `1px solid ${C.line}` : "none" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: C.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email || meta.access}</div>
                    </div>
                    <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                      <Pill tone={meta.tone}>{u.role}</Pill>
                      {isAdmin && (
                        <button onClick={() => removeUser(i)} title="Remove user" className="flex items-center justify-center" style={{ background: "transparent", border: "none", cursor: "pointer", borderRadius: 7, color: C.red, padding: 6 }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = C.redSoft)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>);
              })}
              {isAdmin && (
                <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${C.line}` }}>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10 }}>ADD USER</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={{ ...inputStyle, flex: "1 1 150px", minWidth: 0 }} />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" style={{ ...inputStyle, flex: "1 1 150px", minWidth: 0 }} />
                    <BrandSelect value={newRole} onChange={setNewRole} options={ROLES.map((r) => r.name)} style={{ flex: "1 1 140px", minWidth: 0 }} />
                    <button onClick={addUser} className="flex items-center gap-1.5" style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}><Plus size={15} /> Add</button>
                  </div>
                </div>
              )}
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-3"><ClipboardCheck size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Role permissions</span></div>
              {ROLES.map((r, i) => (
                <div key={r.name} className="flex items-start justify-between gap-3 py-2.5" style={{ borderBottom: i < ROLES.length - 1 ? `1px solid ${C.line}` : "none" }}>
                  <div><div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div><div style={{ fontSize: 11, color: C.faint }}>{r.access}</div></div>
                  <Pill tone={r.tone}>{r.name}</Pill>
                </div>))}
            </Card>
          </div>
        )}

        {tab === "Notifications" && (
          <Card>
            <div className="flex items-center gap-2 mb-3"><Bell size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Notifications</span></div>
            {[["overdue", "Overdue payment reminders", "Alert when a milestone goes overdue"], ["receipts", "Payment receipt emails", "Email a receipt when a payment is recorded"], ["possession", "Possession-ready alerts", "Notify when a unit is ready to hand over"], ["service", "New service request alerts", "Ping the team on new post-handover tickets"]].map(([key, label, desc], i, arr) => (
              <div key={key} className="flex items-center justify-between gap-3 py-2.5" style={{ borderBottom: i < arr.length - 1 ? `1px solid ${C.line}` : "none" }}>
                <div><div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div><div style={{ fontSize: 11, color: C.faint }}>{desc}</div></div>
                <Switch on={notif[key]} set={(v) => setNotif({ ...notif, [key]: v })} />
              </div>))}
          </Card>
        )}

        {tab === "Collections" && (
          <Card>
            <div className="flex items-center gap-2 mb-3"><Wallet size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Collections & penalties</span></div>
            {!isAdmin && <div style={{ fontSize: 12, color: C.faint, marginBottom: 8 }}>Only a Super Admin can change collection rules.</div>}
            <div className="grid gap-x-6 gap-y-3" style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
              <SField label="Penalty rate (% per month)"><input value={coll.penalty} disabled={!isAdmin} onChange={(e) => setColl({ ...coll, penalty: e.target.value })} style={fieldStyle(isAdmin)} /></SField>
              <SField label="Grace period (days)"><input value={coll.grace} disabled={!isAdmin} onChange={(e) => setColl({ ...coll, grace: e.target.value })} style={fieldStyle(isAdmin)} /></SField>
            </div>
            <div className="flex items-center justify-between gap-3 mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
              <div><div style={{ fontSize: 13, fontWeight: 500 }}>Auto-send reminders</div><div style={{ fontSize: 11, color: C.faint }}>SMS + WhatsApp on overdue dues</div></div>
              <Switch on={coll.autoRemind} set={(v) => setColl({ ...coll, autoRemind: v })} disabled={!isAdmin} />
            </div>
          </Card>
        )}

        {tab === "Appearance" && (
          <Card>
            <div className="flex items-center gap-2 mb-3"><Settings size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Appearance</span></div>
            <div className="grid gap-x-6 gap-y-3" style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
              <SField label="Theme"><BrandSelect value={appr.theme} onChange={(v) => setAppr({ ...appr, theme: v })} options={["Light", "System"]} /></SField>
              <SField label="Date format"><BrandSelect value={appr.dateFmt} onChange={(v) => setAppr({ ...appr, dateFmt: v })} options={["DD MMM YYYY", "DD/MM/YYYY", "MM/DD/YYYY"]} /></SField>
              <SField label="Rows per page"><BrandSelect value={appr.rows} onChange={(v) => setAppr({ ...appr, rows: v })} options={["10", "25", "50", "100"]} /></SField>
            </div>
          </Card>
        )}

        {tab === "Audit log" && (
          <Card>
            <div className="flex items-center gap-2 mb-3"><ListChecks size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Audit log</span></div>
            {AUDIT.map((x, i) => (
              <div key={i} className="flex gap-3 pb-3">
                <div className="flex flex-col items-center"><span style={{ width: 8, height: 8, borderRadius: 99, background: C.accent, marginTop: 5 }} />{i < AUDIT.length - 1 && <span style={{ width: 1, flex: 1, background: C.line, marginTop: 2 }} />}</div>
                <div><div style={{ fontSize: 13 }}>{x.what}</div><div style={{ fontSize: 11, color: C.faint }}>{x.who} · {x.when}</div></div>
              </div>))}
          </Card>
        )}
      </div>
    </div>
  );
}

/* ---------------- account detail ---------------- */
function MoneyRow({ label, value, tone, strong }) {
  return (
    <div className="flex items-center justify-between" style={{ padding: "7px 0", borderBottom: `1px solid ${C.line}` }}>
      <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
      <span style={{ fontFamily: display, fontSize: strong ? 15 : 13.5, fontWeight: 600, color: tone || C.ink }}>{value}</span>
    </div>
  );
}
function JourneyRail({ current }) {
  return (
    <div>
      {STAGES.map((s, i) => {
        const done = i < current, here = i === current;
        return (
          <div key={s} className="flex items-start gap-2.5">
            <div className="flex flex-col items-center" style={{ alignSelf: "stretch" }}>
              <div className="flex items-center justify-center rounded-full" style={{ width: 22, height: 22, flexShrink: 0, background: done ? C.accent : C.white, border: `2px solid ${done || here ? C.accent : C.line}` }}>
                {done ? <CheckCircle2 size={13} color="#fff" /> : <span style={{ width: 7, height: 7, borderRadius: 99, background: here ? C.accent : C.faint }} />}
              </div>
              {i < STAGES.length - 1 && <span style={{ width: 2, flex: 1, minHeight: 14, background: i < current ? C.accent : C.line }} />}
            </div>
            <span style={{ fontSize: 12.5, paddingBottom: 12, color: done || here ? C.ink : C.faint, fontWeight: here ? 600 : 400 }}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}
function DetailTab({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: active ? C.accent : "transparent", color: active ? "#fff" : C.muted,
      border: "none", cursor: "pointer", borderRadius: 8, padding: "7px 14px", fontSize: 13,
      fontWeight: active ? 600 : 500, fontFamily: sans,
      boxShadow: active ? "0 1px 3px rgba(110,75,30,0.22)" : "none",
    }}>{label}</button>
  );
}
function Detail({ acc, onBack, onPay, onUpdate }) {
  const m = money(acc);
  const can = useCan();
  const ui = useUI();
  const [tab, setTab] = useState("Overview");
  const survey = acc.survey || {};
  const editable = can("operate");
  const [note, setNote] = useState(survey.notes || "");
  const toggleSurvey = (field, opt) => {
    if (!editable || !onUpdate) return;
    const cur = survey[field] || [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    onUpdate(acc.id, { survey: { ...survey, [field]: next } });
  };
  const saveNote = () => { if (editable && onUpdate && note !== (survey.notes || "")) onUpdate(acc.id, { survey: { ...survey, notes: note } }); };
  const initials = (acc.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const tabs = ["Overview", "Payments", "Documents", "Service", "Activity"];

  const Payments = (
    <Card>
      <div className="flex items-center justify-between mb-3"><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Payment schedule</span><span style={{ fontSize: 11, color: C.accent }} className="flex items-center gap-1"><RefreshCw size={11} /> invoices synced</span></div>
      {acc.schedule.length === 0 && <div style={{ fontSize: 13, color: C.faint }}>No schedule yet.</div>}
      {acc.schedule.map((s, i) => { const pen = penaltyOf(s); return (
        <div key={i} className="flex items-center justify-between py-2.5" style={{ borderBottom: i < acc.schedule.length - 1 ? `1px solid ${C.line}` : "none" }}>
          <div><div style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</div><div className="flex items-center gap-1.5" style={{ fontSize: 11, color: C.faint }}><Calendar size={11} /> due {s.due}{s.receipt ? ` · ${s.receipt}` : ""}</div></div>
          <div className="flex items-center gap-3">
            <div className="text-right"><span style={{ fontFamily: display, fontSize: 14, fontWeight: 600 }}>{inr(s.amt)}</span>{pen > 0 && <div style={{ fontSize: 11, color: C.red }}>+{inr(pen)} penalty</div>}</div>
            {s.status === "paid" ? <Pill tone="paid">paid</Pill> : can("operate") ? <button onClick={async () => { if (await ui.confirm({ title: "Record payment", message: `Record ${inr(s.amt)} for "${s.label}"?\nThis marks the milestone as paid.`, confirmText: "Record payment" })) { onPay(acc.id, i); ui.toast(`Payment recorded · ${s.label}`); } }} style={{ cursor: "pointer", fontSize: 12, fontWeight: 500, padding: "4px 10px", borderRadius: 7, color: "#fff", background: s.status === "overdue" ? C.red : C.accent, border: "none" }}>Record</button> : <Pill tone={s.status}>{s.status}</Pill>}
          </div>
        </div>); })}
      {acc.loan && (
        <div className="mt-3 pt-3 rounded-lg p-3" style={{ borderTop: `1px solid ${C.line}`, background: C.card }}>
          <div className="flex items-center gap-2 mb-2"><Landmark size={14} color={C.accent} /><span style={{ fontSize: 13, fontWeight: 600 }}>Home loan · {acc.loan.bank}</span><Pill tone={acc.loan.status}>{acc.loan.status}</Pill></div>
          <div className="flex justify-between" style={{ fontSize: 12, color: C.muted }}><span>Sanctioned {inr(acc.loan.sanctioned)}</span><span>Disbursed {inr(acc.loan.disbursed)}</span></div>
        </div>)}
    </Card>
  );

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 mb-4" style={{ color: C.muted, fontSize: 13, cursor: "pointer", background: "none", border: "none" }}><ArrowLeft size={15} /> Back</button>

      <div className="grid gap-5" style={{ gridTemplateColumns: "300px minmax(0,1fr)", alignItems: "start" }}>
        {/* sticky summary rail */}
        <div style={{ position: "sticky", top: 0 }}>
          <Card pad={5}>
            <div className="flex items-center gap-3" style={{ marginBottom: 14 }}>
              <div className="flex items-center justify-center rounded-full" style={{ width: 48, height: 48, flexShrink: 0, background: C.accent, color: "#fff", fontFamily: display, fontSize: 18, fontWeight: 600 }}>{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: display, fontSize: 19, fontWeight: 600, lineHeight: 1.15 }}>{acc.name}</div>
                <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, color: C.accent, marginTop: 2 }}>{acc.bookingNo}</div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5" style={{ fontSize: 12.5, color: C.muted, marginBottom: 14 }}>
              <a href={`tel:${acc.phone}`} className="flex items-center gap-2" style={{ color: C.muted, textDecoration: "none" }}><Phone size={13} color={C.accent} /> {acc.phone}</a>
              <a href={`mailto:${acc.email}`} className="flex items-center gap-2" style={{ color: C.muted, textDecoration: "none", wordBreak: "break-all" }}><Mail size={13} color={C.accent} /> {acc.email}</a>
            </div>

            <div className="rounded-lg p-3" style={{ background: C.card, border: `1px solid ${C.line}`, marginBottom: 14 }}>
              <div className="flex items-center justify-between mb-1"><span className="flex items-center gap-1.5" style={{ fontSize: 11.5, color: C.muted }}><MapPin size={12} /> Unit</span><span className="flex items-center gap-1" style={{ fontSize: 10, color: C.accent }}><RefreshCw size={10} /> synced</span></div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{acc.project}</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>{acc.type} · {acc.unit} · {acc.area} · {acc.facing}</div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <MoneyRow label="Sale value" value={inr(acc.salePrice)} strong />
              <MoneyRow label="Collected" value={`${inr(m.paid)} · ${m.pct}%`} tone={C.green} />
              <MoneyRow label="Outstanding" value={inr(m.outstanding)} />
              <div className="flex items-center justify-between" style={{ padding: "7px 0" }}>
                <span style={{ fontSize: 12, color: C.muted }}>Overdue + penalty</span>
                <span style={{ fontFamily: display, fontSize: 13.5, fontWeight: 600, color: m.overdue ? C.red : C.ink }}>{m.overdue ? inr(m.overdue + m.penalty) : "—"}</span>
              </div>
            </div>

            <div style={{ fontSize: 10.5, fontWeight: 600, color: C.faint, letterSpacing: 0.4, marginBottom: 10, textTransform: "uppercase" }}>Journey</div>
            <JourneyRail current={acc.stageIdx} />
          </Card>
        </div>

        {/* tabbed content */}
        <div style={{ minWidth: 0 }}>
          <div className="flex items-center gap-1 flex-wrap" style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 11, padding: 4, marginBottom: 18 }}>
            {tabs.map((t) => <DetailTab key={t} label={t} active={tab === t} onClick={() => setTab(t)} />)}
          </div>

          {tab === "Overview" && (
            <div className="flex flex-col gap-5">
              <Card>
                <div className="flex items-center gap-2 mb-3"><Users size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Customer details</span></div>
                <div className="grid gap-x-6 gap-y-3" style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
                  <Field label="Full name" value={acc.name} />
                  <Field label="Phone" value={acc.phone} />
                  <Field label="Email" value={acc.email} />
                  <Field label="Booking no." value={acc.bookingNo} mono />
                  <Field label="Booking date" value={acc.saleDate} />
                  <Field label="Salesperson" value={acc.salesperson} />
                </div>
              </Card>
              {acc.registration && (
                <Card><div className="flex items-center gap-2 mb-2"><ClipboardCheck size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Registration</span></div>
                  <div className="flex items-center justify-between py-1" style={{ fontSize: 13 }}><span style={{ color: C.muted }}>Status</span><Pill tone={acc.registration.status}>{acc.registration.status}</Pill></div>
                  <div className="flex items-center justify-between py-1" style={{ fontSize: 13 }}><span style={{ color: C.muted }}>Date</span><span>{acc.registration.date}</span></div>
                  <div className="flex items-center justify-between py-1" style={{ fontSize: 13 }}><span style={{ color: C.muted }}>Stamp duty</span><span style={{ fontFamily: display, fontWeight: 600 }}>{inr(acc.registration.stampDuty)}</span></div>
                </Card>)}
              {(acc.futureReqs || []).length > 0 && (
                <Card><div className="flex items-center gap-2 mb-3"><ListChecks size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Future requirements</span></div>
                  {acc.futureReqs.map((f, i) => <div key={i} className="flex items-center justify-between py-2 gap-3" style={{ borderBottom: i < acc.futureReqs.length - 1 ? `1px solid ${C.line}` : "none" }}><span style={{ fontSize: 13 }}>{f.item}</span><span className="flex items-center gap-1.5" style={{ fontSize: 11, color: C.faint, whiteSpace: "nowrap" }}><Calendar size={11} /> {f.when}</span></div>)}
                </Card>)}
            </div>
          )}

          {tab === "Payments" && Payments}

          {tab === "Documents" && (
            <Card><div className="flex items-center gap-2 mb-3"><FileText size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Booking documents</span></div>
              {acc.docs.map((d, i) => <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: i < acc.docs.length - 1 ? `1px solid ${C.line}` : "none" }}><span style={{ fontSize: 13 }}>{d.name}</span><Pill tone={d.status}>{d.status}</Pill></div>)}
            </Card>
          )}

          {tab === "Service" && (
            <div className="flex flex-col gap-5">
              <Card><div className="flex items-center gap-2 mb-3"><Wrench size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Service & warranty</span></div>
                {acc.service.length === 0 ? <div style={{ fontSize: 12, color: C.faint }}>None raised.</div> : acc.service.map((s, i) => <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: i < acc.service.length - 1 ? `1px solid ${C.line}` : "none" }}><div><div style={{ fontSize: 13 }}>{s.title}</div><div style={{ fontSize: 11, color: C.faint }}>{s.date}</div></div><Pill tone={s.status}>{s.status}</Pill></div>)}
              </Card>
              <Card><div className="flex items-center gap-2 mb-3"><AlertTriangle size={15} color={C.amber} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Snag list</span></div>
                {acc.snags.length === 0 ? <div style={{ fontSize: 12, color: C.faint }}>No snags reported.</div> : acc.snags.map((s, i) => <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: i < acc.snags.length - 1 ? `1px solid ${C.line}` : "none" }}><span style={{ fontSize: 13 }}>{s.item}</span><Pill tone={s.status}>{s.status}</Pill></div>)}
              </Card>
            </div>
          )}

          {tab === "Activity" && (
            <Card><H>Activity</H>
              {acc.timeline.map((t, i) => (
                <div key={i} className="flex gap-3 pb-3"><div className="flex flex-col items-center"><span style={{ width: 9, height: 9, borderRadius: 99, background: C.accent, marginTop: 4 }} />{i < acc.timeline.length - 1 && <span style={{ width: 1, flex: 1, background: C.line, marginTop: 2 }} />}</div><div><div style={{ fontSize: 13 }}>{t.text}</div><div style={{ fontSize: 11, color: C.faint }}>{t.date} · {t.by}</div></div></div>))}
            </Card>
          )}

          {/* lead source & decision survey — always shown at the bottom */}
          <Card style={{ marginTop: 20 }}>
            <div className="flex items-center gap-2 mb-1"><Sparkles size={15} color={C.accent} /><span style={{ fontFamily: display, fontSize: 16, fontWeight: 600 }}>Customer insights</span></div>
            <div style={{ fontSize: 12, color: C.faint, marginBottom: 16 }}>{editable ? "Tap to select — saved automatically." : "Read-only."}</div>

            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 9 }}>How did you hear about {BRAND}?</div>
            <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 20 }}>
              {HEARD.map((o) => { const on = (survey.heard || []).includes(o); return (
                <button key={o} type="button" disabled={!editable} onClick={() => toggleSurvey("heard", o)} style={{ ...fChip(on), cursor: editable ? "pointer" : "default", opacity: editable || on ? 1 : 0.6 }}>{o}</button>
              ); })}
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 9 }}>What made you finalise {BRAND}?</div>
            <div className="flex flex-wrap gap-1.5" style={{ marginBottom: 20 }}>
              {FINALISED.map((o) => { const on = (survey.finalised || []).includes(o); return (
                <button key={o} type="button" disabled={!editable} onClick={() => toggleSurvey("finalised", o)} style={{ ...fChip(on), cursor: editable ? "pointer" : "default", opacity: editable || on ? 1 : 0.6 }}>{o}</button>
              ); })}
            </div>

            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 9 }}>Anything else in their words?</div>
            <textarea value={note} disabled={!editable} onChange={(e) => setNote(e.target.value)} onBlur={saveNote} rows={2} placeholder={editable ? "Optional notes from the customer…" : "—"}
              style={{ ...fInp, resize: "vertical", background: editable ? C.white : C.card }} />
          </Card>
        </div>
      </div>
    </div>
  );
}
function Stat({ label, value, tone, synced }) {
  return <div><div className="flex items-center gap-1.5" style={{ fontSize: 11, color: C.muted }}>{label}{synced && <RefreshCw size={9} color={C.accent} />}</div><div style={{ fontFamily: display, fontSize: 17, fontWeight: 600, color: tone || C.ink }}>{value}</div></div>;
}
function Field({ label, value, mono }) {
  return <div><div style={{ fontSize: 11, color: C.muted }}>{label}</div><div style={{ fontSize: 13.5, fontWeight: 500, color: C.ink, marginTop: 2, fontFamily: mono ? "ui-monospace, monospace" : undefined, wordBreak: "break-word" }}>{value}</div></div>;
}
function StageTracker({ current }) {
  return (
    <div className="flex items-center w-full">
      {STAGES.map((s, i) => {
        const done = i < current, here = i === current;
        return (
          <div key={s} className="flex items-center" style={{ flex: i === STAGES.length - 1 ? "0 0 auto" : "1 1 0" }}>
            <div className="flex flex-col items-center" style={{ minWidth: 64 }}>
              <div className="flex items-center justify-center rounded-full" style={{ width: 26, height: 26, background: done ? C.accent : C.white, border: `2px solid ${done || here ? C.accent : C.line}` }}>
                {done ? <CheckCircle2 size={15} color="#fff" /> : <span style={{ width: 8, height: 8, borderRadius: 99, background: here ? C.accent : C.faint }} />}
              </div>
              <span className="mt-1.5 text-center" style={{ fontSize: 11, color: done || here ? C.ink : C.faint, fontWeight: here ? 600 : 400 }}>{s}</span>
            </div>
            {i < STAGES.length - 1 && <div style={{ flex: "1 1 0", height: 2, marginTop: -16, background: i < current ? C.accent : C.line }} />}
          </div>);
      })}
    </div>
  );
}
